import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, FileText, Printer, X, Search, ChevronLeft, ChevronRight, Send, Mail, CheckSquare, Square, Download, Filter, ArrowUpDown, ArrowUp, ArrowDown, Calendar, User, CreditCard, ChevronDown, Upload, Loader2, Eye, Copy, Check, AlertCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { ComprovanteCell } from '../components/ComprovanteCell';
import { getProductSalesUnit } from '../lib/productPricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Formata valores em Real (BRL)
const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Componente de cabeçalho de coluna com ordenação - fora do componente principal
const SortableHeader = ({ label, sortKey, className = '', sortConfig, onSort }) => (
  <th 
    className={`px-4 py-3 text-left text-sm font-semibold cursor-pointer hover:bg-accent/50 transition-colors select-none ${className}`}
    onClick={() => onSort(sortKey)}
  >
    <div className="flex items-center gap-1">
      {label}
      {sortConfig.key === sortKey ? (
        sortConfig.direction === 'asc' ? 
          <ArrowUp className="w-4 h-4 text-primary" /> : 
          <ArrowDown className="w-4 h-4 text-primary" />
      ) : (
        <ArrowUpDown className="w-4 h-4 text-muted-foreground opacity-50" />
      )}
    </div>
  </th>
);

export const Romaneios = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Estados principais
  const [romaneios, setRomaneios] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingRomaneio, setEditingRomaneio] = useState(null);
  // Mini-modal de pagamento rápido (sem abrir o romaneio inteiro)
  const [showQuickPayModal, setShowQuickPayModal] = useState(false);
  const [quickPayRomaneio, setQuickPayRomaneio] = useState(null);
  const [quickPayForm, setQuickPayForm] = useState({ valor: '', forma: 'pix', data: new Date().toISOString().slice(0, 10), observacao: '', pagar_comissao: false });
  const [quickPayComissao, setQuickPayComissao] = useState({ total: 0, paga: 0, pendente: 0, vendedor: '', percentual: 0 });
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  // Seleção múltipla
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // ============ FILTROS E ORDENAÇÃO ============
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'numero_pedido', direction: 'desc' });
  const [filters, setFilters] = useState({
    dataInicio: '',
    dataFim: '',
    vendedor: '',
    cliente: '',
    pagamento: '',
    status: ''
  });
  // Tab: 'vendas' (default — não-orçamento) | 'orcamentos' | 'todos' | 'pendentes'
  const [tipoFiltro, setTipoFiltro] = useState('vendas');
  const [showFilters, setShowFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const [activeColumnFilter, setActiveColumnFilter] = useState(null);
  
  // Modal de edição em massa
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditField, setBulkEditField] = useState('');
  const [bulkEditValue, setBulkEditValue] = useState('');
  
  // Upload de imagem com IA
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    numero_pedido: '',
    data_romaneio: '',
    cliente_nome: '',
    cliente_id: '',
    vendedor_nome: '',
    vendedor_id: '',
    transportadora_nome: '',
    transportadora_id: '',
    items: [],
    desconto_tipo: 'valor',
    desconto_valor: '0',
    forma_pagamento: 'a_receber',
    dias_para_receber: '',
    prazos_pagamento: [],
    parcelas_prazo: ['', '', '', '', '', ''],
    numero_nota_fiscal: '',
    numero_boleto: '',
    pagamento_antecipado: '0',
    observacoes: '',
    is_orcamento: false,
    boletos: []
  });
  
  // Dropdowns
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showSellerDropdown, setShowSellerDropdown] = useState(false);
  const [showCarrierDropdown, setShowCarrierDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  
  // Item sendo adicionado
  const [newItem, setNewItem] = useState({
    produto_nome: '',
    produto_id: '',
    quantidade: '1',
    tipo: 'caixa', // caixa ou pacote (Laços)
    preco_unitario: '',
    valor_total: '0',
    percentual_desconto: '0',
    percentual_comissao: '',
    // Preços do produto selecionado
    preco_unidade: 0,
    preco_centena: 0,
    preco_milheiro: 0,
    preco_caixa: 0
  });

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    try {
      const [romRes, prodRes, cliRes, selRes, carRes] = await Promise.all([
        axios.get(`${API}/romaneios`),
        axios.get(`${API}/products`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/sellers`),
        axios.get(`${API}/carriers`)
      ]);
      setRomaneios(romRes.data);
      setProducts(prodRes.data);
      setClients(cliRes.data);
      setSellers(selRes.data);
      setCarriers(carRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-abrir modal de edição quando vier com ?editId=<id> na URL
  useEffect(() => {
    if (loading || !romaneios.length) return;
    const params = new URLSearchParams(location.search);
    const editId = params.get('editId');
    const tipo = params.get('tipo');
    if (tipo === 'orcamentos' || tipo === 'vendas' || tipo === 'todos') {
      setTipoFiltro(tipo);
    }
    if (editId) {
      const rom = romaneios.find(r => r.id === editId);
      if (rom) {
        openEditModal(rom);
        // Limpar o query param da URL pra não reabrir ao trocar de tela
        navigate('/dashboardadminromaneio/romaneios', { replace: true });
      }
    }
  }, [loading, romaneios, location.search, navigate]);

  // ============ FUNÇÕES DE FILTRO E ORDENAÇÃO ============
  
  // Função de ordenação
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Extrair data para comparação
  const getDataValue = (romaneio) => {
    const data = romaneio.data_criacao || romaneio.created_at || '';
    if (typeof data === 'string') return data.substring(0, 10);
    if (data instanceof Date) return data.toISOString().substring(0, 10);
    return '';
  };

  // Aplicar filtros e ordenação
  const filteredAndSortedRomaneios = useMemo(() => {
    let result = [...romaneios];
    
    // Filtro de busca geral
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r => 
        (r.numero_pedido || '').toLowerCase().includes(term) ||
        (r.cliente_nome || '').toLowerCase().includes(term) ||
        (r.vendedor_nome || '').toLowerCase().includes(term)
      );
    }
    
    // Filtro por data
    if (filters.dataInicio) {
      result = result.filter(r => {
        const data = getDataValue(r);
        return data >= filters.dataInicio;
      });
    }
    if (filters.dataFim) {
      result = result.filter(r => {
        const data = getDataValue(r);
        return data <= filters.dataFim;
      });
    }
    
    // Filtro por vendedor
    if (filters.vendedor) {
      result = result.filter(r => r.vendedor_nome === filters.vendedor);
    }
    
    // Filtro por cliente
    if (filters.cliente) {
      result = result.filter(r => 
        (r.cliente_nome || '').toLowerCase().includes(filters.cliente.toLowerCase())
      );
    }
    
    // Filtro por forma de pagamento
    if (filters.pagamento) {
      result = result.filter(r => r.forma_pagamento === filters.pagamento);
    }

    // Filtro por tipo (vendas / orçamentos / pendentes / todos)
    if (tipoFiltro === 'vendas') {
      result = result.filter(r => !r.is_orcamento);
    } else if (tipoFiltro === 'orcamentos') {
      result = result.filter(r => !!r.is_orcamento);
    } else if (tipoFiltro === 'pendentes') {
      result = result.filter(r => !!r.pendente_validacao_admin);
    }
    
    // Ordenação
    result.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'numero_pedido':
          aValue = parseInt((a.numero_pedido || '0').replace(/\D/g, '')) || 0;
          bValue = parseInt((b.numero_pedido || '0').replace(/\D/g, '')) || 0;
          break;
        case 'data':
          aValue = getDataValue(a);
          bValue = getDataValue(b);
          break;
        case 'cliente_nome':
          aValue = (a.cliente_nome || '').toLowerCase();
          bValue = (b.cliente_nome || '').toLowerCase();
          break;
        case 'vendedor_nome':
          aValue = (a.vendedor_nome || '').toLowerCase();
          bValue = (b.vendedor_nome || '').toLowerCase();
          break;
        case 'valor_total':
          aValue = a.valor_total || 0;
          bValue = b.valor_total || 0;
          break;
        case 'forma_pagamento':
          aValue = (a.forma_pagamento || '').toLowerCase();
          bValue = (b.forma_pagamento || '').toLowerCase();
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [romaneios, searchTerm, filters, sortConfig, tipoFiltro]);

  // Obter valores únicos para filtros
  const uniqueVendedores = useMemo(() => {
    const vendedores = [...new Set(romaneios.map(r => r.vendedor_nome).filter(Boolean))];
    return vendedores.sort();
  }, [romaneios]);

  const uniqueFormasPagamento = [
    { value: 'a_receber', label: 'A Receber' },
    { value: 'pago', label: 'Pago' },
    { value: 'avista', label: 'À Vista' },
    { value: 'boleto', label: 'Boleto' },
    { value: 'pix', label: 'PIX' },
    { value: 'cartao', label: 'Cartão' },
    { value: 'prazo', label: 'A Prazo' }
  ];

  // Limpar filtros
  const clearFilters = () => {
    setFilters({
      dataInicio: '',
      dataFim: '',
      vendedor: '',
      cliente: '',
      pagamento: '',
      status: ''
    });
    setSearchTerm('');
  };

  // Edição em massa
  const handleBulkEdit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um romaneio');
      return;
    }
    if (!bulkEditField || !bulkEditValue) {
      toast.error('Selecione o campo e valor para alterar');
      return;
    }

    try {
      let updated = 0;
      for (const id of selectedIds) {
        const romaneio = romaneios.find(r => r.id === id);
        if (romaneio) {
          const updateData = { ...romaneio };
          
          if (bulkEditField === 'vendedor') {
            const seller = sellers.find(s => s.id === bulkEditValue);
            updateData.vendedor_id = bulkEditValue;
            updateData.vendedor_nome = seller?.nome || '';
          } else if (bulkEditField === 'forma_pagamento') {
            updateData.forma_pagamento = bulkEditValue;
          } else if (bulkEditField === 'status_pagamento') {
            updateData.status_pagamento = bulkEditValue;
          }
          
          await axios.put(`${API}/romaneios/${id}`, updateData);
          updated++;
        }
      }
      
      toast.success(`${updated} romaneio(s) atualizado(s)!`);
      setShowBulkEditModal(false);
      setBulkEditField('');
      setBulkEditValue('');
      setSelectedIds([]);
      setSelectAll(false);
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao atualizar romaneios');
      console.error(error);
    }
  };

  // Upload de imagem para análise com IA
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem');
      return;
    }
    
    setUploadingImage(true);
    toast.info('Analisando imagem com IA...');
    
    try {
      // Converter imagem para base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Image = reader.result;
        setUploadedImageUrl(base64Image);
        
        try {
          // Enviar para o backend para análise
          const response = await axios.post(`${API}/romaneios/analisar-imagem`, {
            image_base64: base64Image
          });
          
          const dadosExtraidos = response.data;
          
          // Encontrar IDs correspondentes
          const clienteEncontrado = clients.find(c => 
            c.nome?.toLowerCase().includes(dadosExtraidos.cliente_nome?.toLowerCase() || '') ||
            dadosExtraidos.cliente_nome?.toLowerCase().includes(c.nome?.toLowerCase() || '')
          );
          
          const vendedorEncontrado = sellers.find(s => 
            s.nome?.toLowerCase().includes(dadosExtraidos.vendedor_nome?.toLowerCase() || '') ||
            dadosExtraidos.vendedor_nome?.toLowerCase().includes(s.nome?.toLowerCase() || '')
          );
          
          // Processar itens
          const itensProcessados = (dadosExtraidos.items || []).map(item => {
            const produtoEncontrado = products.find(p => 
              p.produto?.toLowerCase().includes(item.produto_nome?.toLowerCase() || '') ||
              item.produto_nome?.toLowerCase().includes(p.produto?.toLowerCase() || '') ||
              p.codigo === item.codigo
            );
            
            return {
              produto_nome: item.produto_nome || produtoEncontrado?.produto || '',
              produto_id: produtoEncontrado?.id || '',
              quantidade: item.quantidade || 1,
              valor_unitario: item.valor_unitario || produtoEncontrado?.valor_por_1000 || 0,
              valor_total: item.valor_total || 0,
              tipo_entrada: item.tipo || 'caixa'
            };
          });
          
          // Preparar dados para preview
          setPreviewData({
            numero_pedido: dadosExtraidos.numero_pedido || getNextPedidoNumber() + '-26',
            data_romaneio: dadosExtraidos.data || new Date().toISOString().split('T')[0],
            cliente_nome: clienteEncontrado?.nome || dadosExtraidos.cliente_nome || '',
            cliente_id: clienteEncontrado?.id || '',
            vendedor_nome: vendedorEncontrado?.nome || dadosExtraidos.vendedor_nome || '',
            vendedor_id: vendedorEncontrado?.id || '',
            transportadora_nome: dadosExtraidos.transportadora_nome || '',
            transportadora_id: '',
            items: itensProcessados,
            desconto_tipo: 'valor',
            desconto_valor: dadosExtraidos.desconto || '0',
            forma_pagamento: dadosExtraidos.forma_pagamento || 'a_receber',
            valor_total: dadosExtraidos.valor_total || 0,
            observacoes: dadosExtraidos.observacoes || ''
          });
          
          setShowPreviewModal(true);
          toast.success('Imagem analisada com sucesso!');
        } catch (error) {
          console.error('Erro ao analisar imagem:', error);
          toast.error('Erro ao analisar imagem. Tente novamente.');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      toast.error('Erro ao processar imagem');
    } finally {
      setUploadingImage(false);
      e.target.value = ''; // Reset input
    }
  };

  // Salvar romaneio do preview
  const handleSavePreview = async () => {
    if (!previewData) return;
    
    try {
      const romaneioData = {
        ...previewData,
        data_criacao: previewData.data_romaneio,
        created_at: new Date().toISOString()
      };
      
      await axios.post(`${API}/romaneios`, romaneioData);
      toast.success('Romaneio salvo com sucesso!');
      setShowPreviewModal(false);
      setPreviewData(null);
      setUploadedImageUrl('');
      fetchAllData();
    } catch (error) {
      console.error('Erro ao salvar romaneio:', error);
      toast.error('Erro ao salvar romaneio');
    }
  };

  // Gerar próximo número de pedido
  const getNextPedidoNumber = () => {
    if (romaneios.length === 0) return '0001';
    const numbers = romaneios
      .map(r => parseInt(r.numero_pedido?.replace(/\D/g, '') || '0'))
      .filter(n => !isNaN(n));
    const maxNum = Math.max(...numbers, 0);
    return String(maxNum + 1).padStart(4, '0');
  };

  // Abrir modal para novo romaneio
  const openNewModal = () => {
    setEditingRomaneio(null);
    const hoje = new Date().toISOString().split('T')[0];
    setFormData({
      numero_pedido: getNextPedidoNumber(),
      data_romaneio: hoje,
      cliente_nome: '',
      cliente_id: '',
      vendedor_nome: '',
      vendedor_id: '',
      transportadora_nome: '',
      transportadora_id: '',
      items: [],
      desconto_tipo: 'valor',
      desconto_valor: '0',
      forma_pagamento: 'a_receber',
      dias_para_receber: '',
      prazos_pagamento: [],
      parcelas_prazo: ['', '', '', '', '', ''],
      numero_nota_fiscal: '',
      numero_boleto: '',
      pagamento_antecipado: '0',
      observacoes: '',
      is_orcamento: false,
      boletos: []
    });
    setNewItem({
      produto_nome: '',
      produto_id: '',
      quantidade: '1',
      tipo: 'caixa',
      preco_unitario: '',
      valor_total: '0',
      preco_unidade: 0,
      preco_centena: 0,
      preco_milheiro: 0,
      preco_caixa: 0
    });
    setShowModal(true);
  };

  // Abrir modal para editar
  const openEditModal = (romaneio) => {
    setEditingRomaneio(romaneio);
    // Formatar a data para o input date (YYYY-MM-DD)
    let dataRomaneio = '';
    if (romaneio.created_at) {
      const d = new Date(romaneio.created_at);
      dataRomaneio = d.toISOString().split('T')[0];
    }
    // Carregar parcelas existentes ou criar array vazio
    const parcelas = romaneio.parcelas_prazo || ['', '', '', '', '', ''];
    // Garantir que sempre tenha 6 elementos
    while (parcelas.length < 6) parcelas.push('');
    
    setFormData({
      numero_pedido: romaneio.numero_pedido || '',
      data_romaneio: dataRomaneio,
      cliente_nome: romaneio.cliente_nome || '',
      cliente_id: romaneio.cliente_id || '',
      vendedor_nome: romaneio.vendedor_nome || '',
      vendedor_id: romaneio.vendedor_id || '',
      transportadora_nome: romaneio.transportadora_nome || '',
      transportadora_id: romaneio.transportadora_id || '',
      items: romaneio.items || [],
      desconto_tipo: romaneio.desconto_tipo || 'valor',
      desconto_valor: String(romaneio.desconto_valor || romaneio.valor_frete || 0),
      forma_pagamento: romaneio.forma_pagamento || 'a_receber',
      dias_para_receber: romaneio.dias_para_receber || '',
      prazos_pagamento: romaneio.prazos_pagamento || [],
      parcelas_prazo: parcelas,
      numero_nota_fiscal: romaneio.numero_nota_fiscal || '',
      numero_boleto: romaneio.numero_boleto || '',
      pagamento_antecipado: String(romaneio.pagamento_antecipado || 0),
      observacoes: romaneio.observacoes || '',
      is_orcamento: !!romaneio.is_orcamento,
      boletos: romaneio.boletos || []
    });
    setShowModal(true);
  };

  // Calcular valor do item
  const calcularValorItem = (qtd, preco) => {
    const quantidade = parseFloat(qtd) || 0;
    const precoUnit = parseFloat(preco) || 0;
    return (quantidade * precoUnit).toFixed(2);
  };
  
  // Calcular desconto
  const calcularDesconto = () => {
    const subtotal = formData.items.reduce((acc, item) => acc + (item.valor_total || 0), 0);
    const descontoValor = parseFloat(formData.desconto_valor) || 0;
    
    if (formData.desconto_tipo === 'percentual') {
      return (subtotal * descontoValor / 100);
    }
    return descontoValor;
  };

  // Adicionar item à lista
  const addItem = () => {
    if (!newItem.produto_nome) {
      toast.error('Digite o nome do produto');
      return;
    }
    if (!newItem.quantidade || parseFloat(newItem.quantidade) <= 0) {
      toast.error('Digite uma quantidade válida');
      return;
    }
    if (!newItem.preco_unitario || parseFloat(newItem.preco_unitario) <= 0) {
      toast.error('Digite um preço válido');
      return;
    }

    // Calcular valor com APENAS desconto aplicado (comissão é apenas informação)
    const precoBase = parseFloat(newItem.preco_unitario);
    const percentualDesconto = parseFloat(newItem.percentual_desconto) || 0;
    const percentualComissao = parseFloat(newItem.percentual_comissao) || 0;
    // Comissão NÃO desconta do valor - apenas o desconto é aplicado
    const precoComDesconto = precoBase * (1 - percentualDesconto / 100);
    const valorTotal = calcularValorItem(newItem.quantidade, precoComDesconto.toFixed(2));
    
    const item = {
      produto_id: newItem.produto_id || null,
      produto_nome: newItem.produto_nome,
      quantidade: parseFloat(newItem.quantidade),
      tipo_entrada: newItem.tipo,
      valor_unitario: precoBase,
      valor_unitario_com_desconto: precoComDesconto,
      percentual_desconto: percentualDesconto,
      percentual_comissao: percentualComissao, // Apenas informação para o vendedor
      valor_total: parseFloat(valorTotal),
      descricao_tipo: `${newItem.quantidade} ${newItem.tipo === 'unidade' ? 'un' : newItem.tipo === 'pacote' ? 'pct' : 'cx'}`
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));

    // Limpar form do item
    setNewItem({
      produto_nome: '',
      produto_id: '',
      quantidade: '1',
      tipo: 'caixa',
      preco_unitario: '',
      valor_total: '0',
      percentual_desconto: '0',
      percentual_comissao: '',
      preco_unidade: 0,
      preco_centena: 0,
      preco_milheiro: 0,
      preco_caixa: 0
    });
    
    toast.success('Produto adicionado!');
  };

  // Remover item
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calcular total do romaneio (subtrai desconto geral e pagamento antecipado)
  const calcularTotal = () => {
    const totalItems = formData.items.reduce((sum, item) => sum + (item.valor_total || 0), 0);
    const desconto = calcularDesconto();
    const antecipado = parseFloat(formData.pagamento_antecipado) || 0;
    return Math.max(0, totalItems - desconto - antecipado).toFixed(2);
  };

  // Salvar romaneio
  const handleSubmit = async () => {
    if (!formData.cliente_nome) {
      toast.error('Digite o nome do cliente');
      return;
    }

    try {
      const desconto = calcularDesconto();
      const data = {
        ...formData,
        desconto_tipo: formData.desconto_tipo,
        desconto_valor: parseFloat(formData.desconto_valor) || 0,
        desconto_calculado: desconto,
        pagamento_antecipado: parseFloat(formData.pagamento_antecipado) || 0,
        valor_total: parseFloat(calcularTotal())
      };
      
      // Se tem data do romaneio, converter para created_at
      if (formData.data_romaneio) {
        data.created_at = new Date(formData.data_romaneio + 'T12:00:00').toISOString();
      }

      if (editingRomaneio) {
        await axios.put(`${API}/romaneios/${editingRomaneio.id}`, data);
        toast.success('Romaneio atualizado!');
      } else {
        await axios.post(`${API}/romaneios`, data);
        toast.success('Romaneio criado!');
      }
      
      setShowModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao salvar romaneio');
      console.error(error);
    }
  };

  // Deletar romaneio
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este romaneio?')) return;
    
    try {
      await axios.delete(`${API}/romaneios/${id}`);
      toast.success('Romaneio deletado!');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao deletar');
    }
  };

  // Converter orçamento em romaneio (faturado)
  const handleConverterOrcamento = async (romaneio) => {
    if (!window.confirm(`Converter o orçamento #${romaneio.numero_pedido} em Romaneio? Ele passará a contar no faturamento.`)) return;
    try {
      await axios.post(`${API}/romaneios/${romaneio.id}/converter-orcamento`);
      toast.success('Orçamento convertido em Romaneio!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao converter orçamento');
    }
  };

  // Abrir mini-modal de pagamento rápido
  const openQuickPay = (romaneio) => {
    const valorPago = parseFloat(romaneio.valor_pago || 0);
    const valorTotal = parseFloat(romaneio.valor_total || 0);
    const saldo = Math.max(0, valorTotal - valorPago);

    // Buscar percentual de comissão do vendedor para calcular pendente
    const vendedor = sellers.find(s => s.id === romaneio.vendedor_id || s.nome === romaneio.vendedor_nome);
    const percentual = vendedor?.percentual_comissao || 0;
    const comissaoTotal = (valorTotal * percentual) / 100;
    const comissaoPaga = parseFloat(romaneio.comissao_paga || 0);
    const comissaoPendente = Math.max(0, comissaoTotal - comissaoPaga);

    setQuickPayComissao({
      total: comissaoTotal,
      paga: comissaoPaga,
      pendente: comissaoPendente,
      vendedor: vendedor?.nome || romaneio.vendedor_nome || '',
      percentual,
    });
    setQuickPayRomaneio(romaneio);
    setQuickPayForm({
      valor: saldo.toFixed(2),
      forma: 'pix',
      data: new Date().toISOString().slice(0, 10),
      observacao: '',
      pagar_comissao: false
    });
    setShowQuickPayModal(true);
  };

  const submitQuickPay = async () => {
    if (!quickPayRomaneio) return;
    const valor = parseFloat(quickPayForm.valor);
    if (!valor || valor <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    try {
      // 1) Registra pagamento do romaneio
      await axios.post(`${API}/romaneios/${quickPayRomaneio.id}/registrar-pagamento`, {
        valor_pago: valor,
        data_pagamento: quickPayForm.data,
        observacao: `${quickPayForm.forma.toUpperCase()}${quickPayForm.observacao ? ' - ' + quickPayForm.observacao : ''}`
      });

      // 2) Se marcou "pagar comissão", registra também a comissão pendente
      let extraMsg = '';
      if (quickPayForm.pagar_comissao && quickPayComissao.pendente > 0) {
        try {
          await axios.post(`${API}/romaneios/${quickPayRomaneio.id}/registrar-comissao`, {
            valor_pago: quickPayComissao.pendente,
            data_pagamento: quickPayForm.data,
            forma_pagamento: quickPayForm.forma,
            observacao: `Pagamento automático junto ao romaneio${quickPayForm.observacao ? ' - ' + quickPayForm.observacao : ''}`
          });
          extraMsg = ` + Comissão de R$ ${quickPayComissao.pendente.toFixed(2)} paga a ${quickPayComissao.vendedor}`;
        } catch (errComissao) {
          console.error('Erro ao registrar comissão:', errComissao);
          toast.error('Pagamento OK, mas falhou ao registrar comissão');
        }
      }

      toast.success(`Pagamento de R$ ${valor.toFixed(2)} registrado!${extraMsg}`);
      setShowQuickPayModal(false);
      setQuickPayRomaneio(null);
      fetchAllData();
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      toast.error('Erro ao registrar pagamento');
    }
  };

  // Imprimir PDF - abre em nova aba para impressão direta
  const handlePrintPDF = async (romaneio) => {
    try {
      const response = await axios.get(`${API}/romaneios/${romaneio.id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Abrir em nova aba para impressão
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Abrindo para impressão...');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
      console.error(error);
    }
  };

  // Imprimir 2 vias do mesmo romaneio em uma única folha
  const handlePrint2Vias = async (romaneio) => {
    try {
      toast.loading('Gerando PDF com 2 vias...');
      
      const response = await axios.get(`${API}/romaneios/${romaneio.id}/pdf-2vias`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      toast.dismiss();
      
      // Abrir em nova aba para impressão
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('PDF com 2 vias gerado!');
    } catch (error) {
      toast.dismiss();
      toast.error('Erro ao gerar PDF com 2 vias');
      console.error(error);
    }
  };

  // Compartilhar via WhatsApp - baixa o PDF e usa Web Share API para enviar como anexo
  const handleShareWhatsApp = async (romaneio) => {
    try {
      toast.loading('Preparando PDF para envio...');
      
      // Baixar o PDF
      const pdfUrl = `${API}/romaneios/${romaneio.id}/pdf`;
      const response = await axios.get(pdfUrl, { responseType: 'blob' });
      
      // Criar arquivo PDF
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `Romaneio_${romaneio.numero_pedido}_${romaneio.cliente_nome.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      
      // Texto simples para acompanhar o PDF
      const texto = `Romaneio ${romaneio.numero_pedido}\nCliente: ${romaneio.cliente_nome}`;
      
      // Verificar se o navegador suporta Web Share API com arquivos
      if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        toast.dismiss();
        await navigator.share({
          title: `Romaneio ${romaneio.numero_pedido}`,
          text: texto,
          files: [pdfFile]
        });
        toast.success('PDF compartilhado!');
      } else {
        // Fallback: baixar o PDF e abrir WhatsApp com mensagem
        toast.dismiss();
        
        // Baixar o PDF automaticamente
        const downloadUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
        toast.success('PDF baixado! Anexe-o na conversa do WhatsApp.');
        
        // Abrir WhatsApp com mensagem simples (sem link)
        const whatsappText = `Romaneio ${romaneio.numero_pedido}\nCliente: ${romaneio.cliente_nome}\n\n📎 PDF em anexo`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
        
        // Pequeno delay para o usuário ver a mensagem
        setTimeout(() => {
          window.open(whatsappUrl, '_blank');
        }, 500);
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Erro ao preparar PDF');
      console.error(error);
    }
  };

  // Compartilhar via Email - Abre modal para envio via Gmail SMTP
  const [emailModal, setEmailModal] = useState({
    open: false,
    romaneio: null,
    to: '',
    whatsapp: '',
    subject: '',
    message: '',
    attachPdf: true,
    sending: false
  });

  const handleShareEmail = async (romaneio) => {
    const cliente = clients.find(c => c.id === romaneio.cliente_id) || {};
    const dataStr = new Date(romaneio.created_at).toLocaleDateString('pt-BR');
    const total = (romaneio.valor_total || 0).toFixed(2).replace('.', ',');
    const subject = `Romaneio ${romaneio.numero_pedido} - ${romaneio.cliente_nome}`;
    const message = `Olá ${romaneio.cliente_nome},\n\n` +
      `Segue em anexo o romaneio ${romaneio.numero_pedido} emitido em ${dataStr}.\n\n` +
      `Valor total: R$ ${total}\n\n` +
      `Qualquer dúvida, estamos à disposição.\n\n` +
      `Atenciosamente,\n` +
      `Distribuidora Santa Ana`;

    setEmailModal({
      open: true,
      romaneio,
      to: cliente.email || '',
      whatsapp: cliente.whatsapp || cliente.telefone || '',
      subject,
      message,
      attachPdf: true,
      sending: false
    });
  };

  const handleSendEmail = async () => {
    if (!emailModal.to || !emailModal.to.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    setEmailModal(prev => ({ ...prev, sending: true }));
    try {
      const resp = await axios.post(`${API}/romaneios/${emailModal.romaneio.id}/enviar-email`, {
        to: emailModal.to,
        subject: emailModal.subject,
        message: emailModal.message,
        attach_pdf: emailModal.attachPdf
      });
      toast.success(resp.data.message || 'Email enviado!');
      setEmailModal({ open: false, romaneio: null, to: '', whatsapp: '', subject: '', message: '', attachPdf: true, sending: false });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao enviar';
      toast.error(detail);
      if (detail.toLowerCase().includes('não configurado')) {
        toast.info('Vá em Configurações → Envio de Email (Gmail) para configurar.');
      }
      setEmailModal(prev => ({ ...prev, sending: false }));
    }
  };

  // Selecionar produto do autocomplete
  const selectProduct = (product) => {
    // Pegar todos os preços disponíveis do produto
    const precoUnidade = product.valor_por_unidade || product.valor_unitario || 0;
    const precoCaixa = product.valor_caixa || product.valor_por_caixa || 0;
    const precoPacote = product.valor_pacote || product.valor_por_pacote || precoCaixa; // Laços geralmente reusa valor_caixa
    const precoCento = product.valor_por_100 || 0;
    const precoMilheiro = product.valor_por_1000 || product.valor_mil || 0;

    // Default: usa o tipo "caixa" se existir, senão "unidade"
    const unit = getProductSalesUnit(product);
    const tipoDefault = unit.tipo === 'pacote' ? 'pacote' : (precoCaixa > 0 ? 'caixa' : 'unidade');
    const precoSelecionado = tipoDefault === 'unidade' ? precoUnidade
                            : tipoDefault === 'pacote' ? precoPacote
                            : tipoDefault === 'cento' ? precoCento
                            : tipoDefault === 'milheiro' ? precoMilheiro
                            : precoCaixa;

    setNewItem(prev => ({
      ...prev,
      produto_id: product.id,
      produto_nome: `${product.codigo || ''} ${product.produto}`.trim(),
      preco_unitario: precoSelecionado > 0 ? precoSelecionado.toFixed(2) : '',
      preco_unidade: precoUnidade,
      preco_centena: precoCento,
      preco_milheiro: precoMilheiro,
      preco_caixa: precoCaixa,
      preco_pacote: precoPacote,
      tipo: tipoDefault
    }));
    setShowProductDropdown(false);
  };
  
  // Atualizar preço quando tipo mudar
  const handleTipoChange = (novoTipo) => {
    const produto = products.find(p => p.id === newItem.produto_id);
    let precoUnidade = newItem.preco_unidade;
    let precoCaixa = newItem.preco_caixa;
    let precoPacote = newItem.preco_pacote || precoCaixa;
    let precoCento = newItem.preco_centena;
    let precoMilheiro = newItem.preco_milheiro;
    if (produto) {
      precoUnidade = precoUnidade || produto.valor_por_unidade || produto.valor_unitario || 0;
      precoCaixa = precoCaixa || produto.valor_caixa || produto.valor_por_caixa || 0;
      precoPacote = precoPacote || produto.valor_pacote || precoCaixa;
      precoCento = precoCento || produto.valor_por_100 || 0;
      precoMilheiro = precoMilheiro || produto.valor_por_1000 || produto.valor_mil || 0;
    }
    const precoSelecionado = novoTipo === 'unidade' ? precoUnidade
                            : novoTipo === 'pacote' ? precoPacote
                            : novoTipo === 'cento' ? precoCento
                            : novoTipo === 'milheiro' ? precoMilheiro
                            : precoCaixa;
    
    setNewItem(prev => ({
      ...prev,
      tipo: novoTipo,
      preco_unitario: precoSelecionado > 0 ? precoSelecionado.toFixed(2) : prev.preco_unitario,
      preco_unidade: precoUnidade,
      preco_centena: precoCento,
      preco_milheiro: precoMilheiro,
      preco_caixa: precoCaixa,
      preco_pacote: precoPacote
    }));
  };
  
  // Editar item já adicionado
  const editItem = (index) => {
    const item = formData.items[index];
    // Buscar preços do produto
    const produto = products.find(p => 
      p.id === item.produto_id || 
      p.produto === item.produto_nome ||
      `${p.codigo || ''} ${p.produto}`.trim() === item.produto_nome
    );
    
    const unit = produto ? getProductSalesUnit(produto) : { tipo: 'caixa', preco: 0 };
    const precoCaixa = unit.preco;
    
    setNewItem({
      produto_nome: item.produto_nome,
      produto_id: item.produto_id || produto?.id || '',
      quantidade: String(item.quantidade || 1),
      tipo: item.tipo_entrada || unit.tipo,
      preco_unitario: String(item.valor_unitario || ''),
      valor_total: String(item.valor_total || 0),
      percentual_desconto: String(item.percentual_desconto || 0),
      percentual_comissao: String(item.percentual_comissao || ''),
      preco_unidade: 0,
      preco_centena: 0,
      preco_milheiro: 0,
      preco_caixa: precoCaixa
    });
    
    // Remover o item da lista para ser re-adicionado após edição
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Paginação - usar dados filtrados
  const totalPages = Math.ceil(filteredAndSortedRomaneios.length / itemsPerPage);
  const paginatedRomaneios = filteredAndSortedRomaneios.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, sortConfig, tipoFiltro]);

  // Funções de seleção múltipla
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(paginatedRomaneios.map(r => r.id));
      setSelectAll(true);
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(i => i !== id));
      setSelectAll(false);
    } else {
      setSelectedIds(prev => [...prev, id]);
      if (selectedIds.length + 1 === paginatedRomaneios.length) {
        setSelectAll(true);
      }
    }
  };

  // Ações em massa
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um romaneio');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir ${selectedIds.length} romaneio(s)?`)) {
      return;
    }

    try {
      let deleted = 0;
      for (const id of selectedIds) {
        await axios.delete(`${API}/romaneios/${id}`);
        deleted++;
      }
      toast.success(`${deleted} romaneio(s) excluído(s)!`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao excluir romaneios');
      console.error(error);
    }
  };

  const handleBulkPrint = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um romaneio');
      return;
    }

    toast.info(`Gerando PDF de ${selectedIds.length} romaneio(s)...`);
    
    for (const id of selectedIds) {
      const romaneio = romaneios.find(r => r.id === id);
      if (romaneio) {
        await handlePrintPDF(romaneio);
      }
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um romaneio');
      return;
    }

    toast.info(`Baixando ${selectedIds.length} romaneio(s)...`);
    
    for (const id of selectedIds) {
      const romaneio = romaneios.find(r => r.id === id);
      if (romaneio) {
        try {
          const response = await axios.get(`${API}/romaneios/${romaneio.id}/pdf`, {
            responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `romaneio_${romaneio.numero_pedido || romaneio.id}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Erro ao baixar PDF:', error);
        }
      }
    }
    
    toast.success(`${selectedIds.length} arquivo(s) baixado(s)!`);
  };

  // Imprimir 2 romaneios em 1 folha
  const handlePrintDuplo = async () => {
    if (selectedIds.length !== 2) {
      toast.error('Selecione exatamente 2 romaneios');
      return;
    }

    toast.info('Gerando PDF com 2 romaneios em 1 folha...');
    
    try {
      const response = await axios.post(`${API}/romaneios/pdf-duplo`, {
        romaneio_id_1: selectedIds[0],
        romaneio_id_2: selectedIds[1]
      }, {
        responseType: 'blob'
      });
      
      // Abrir em nova aba para impressão
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      
      toast.success('PDF duplo gerado! Você pode cortar a folha no meio após imprimir.');
    } catch (error) {
      console.error('Erro ao gerar PDF duplo:', error);
      toast.error('Erro ao gerar PDF duplo');
    }
  };

  // Impressão Inteligente - agrupa romaneios automaticamente
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printPreviewData, setPrintPreviewData] = useState(null);
  const [loadingPrintPreview, setLoadingPrintPreview] = useState(false);

  const handlePrintInteligente = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um romaneio');
      return;
    }

    setLoadingPrintPreview(true);
    
    try {
      // Primeiro, calcular quantas páginas serão necessárias
      const response = await axios.post(`${API}/romaneios/calcular-impressao`, {
        romaneio_ids: selectedIds
      });
      
      setPrintPreviewData(response.data);
      setShowPrintPreview(true);
    } catch (error) {
      console.error('Erro ao calcular impressão:', error);
      toast.error('Erro ao calcular impressão');
    } finally {
      setLoadingPrintPreview(false);
    }
  };

  const confirmarPrintInteligente = async () => {
    try {
      toast.info('Gerando PDF otimizado...');
      
      const response = await axios.post(`${API}/romaneios/pdf-inteligente`, {
        romaneio_ids: selectedIds
      }, {
        responseType: 'blob'
      });
      
      // Abrir em nova aba para impressão
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      
      setShowPrintPreview(false);
      toast.success(`PDF gerado! ${printPreviewData?.economia_papel > 0 ? `Você economizou ${printPreviewData.economia_papel} folha(s)!` : ''}`);
    } catch (error) {
      console.error('Erro ao gerar PDF inteligente:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-heading font-bold">Romaneios</h2>
          <p className="text-muted-foreground mt-1">
            {filteredAndSortedRomaneios.length} de {romaneios.length} romaneios
          </p>
        </div>
        <button onClick={openNewModal} className="btn-primary flex items-center gap-2" data-testid="new-romaneio-btn">
          <Plus className="w-5 h-5" />
          Novo Romaneio
        </button>
      </div>

      {/* Tabs: Vendas / Orçamentos / Pendentes / Todos */}
      <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg w-fit flex-wrap">
        {(() => {
          const qtdVendas = romaneios.filter(r => !r.is_orcamento).length;
          const qtdOrcamentos = romaneios.filter(r => r.is_orcamento).length;
          const qtdPendentes = romaneios.filter(r => r.pendente_validacao_admin).length;
          return (
            <>
              <button
                onClick={() => setTipoFiltro('vendas')}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${tipoFiltro === 'vendas' ? 'bg-white dark:bg-gray-800 shadow text-blue-600' : 'text-gray-600 hover:bg-white/50'}`}
                data-testid="tab-vendas"
              >
                Vendas ({qtdVendas})
              </button>
              <button
                onClick={() => setTipoFiltro('orcamentos')}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors flex items-center gap-1 ${tipoFiltro === 'orcamentos' ? 'bg-white dark:bg-gray-800 shadow text-amber-600' : 'text-gray-600 hover:bg-white/50'}`}
                data-testid="tab-orcamentos"
              >
                📋 Orçamentos ({qtdOrcamentos})
              </button>
              {qtdPendentes > 0 && (
                <button
                  onClick={() => setTipoFiltro('pendentes')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors flex items-center gap-1 relative ${tipoFiltro === 'pendentes' ? 'bg-white dark:bg-gray-800 shadow text-red-600' : 'text-red-600 hover:bg-white/50 animate-pulse'}`}
                  data-testid="tab-pendentes"
                >
                  ⚠️ Pendentes ({qtdPendentes})
                  {tipoFiltro !== 'pendentes' && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
                  )}
                </button>
              )}
              <button
                onClick={() => setTipoFiltro('todos')}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${tipoFiltro === 'todos' ? 'bg-white dark:bg-gray-800 shadow text-gray-700' : 'text-gray-600 hover:bg-white/50'}`}
                data-testid="tab-todos"
              >
                Todos ({romaneios.length})
              </button>
            </>
          );
        })()}
      </div>

      {/* Barra de Busca e Filtros */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        {/* LINHA 1: Botão de busca expansível + ações */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Busca expansível (clique no ícone abre o input ao lado) */}
          <div
            className={`relative flex items-center transition-all duration-300 ease-out overflow-hidden ${
              searchExpanded || searchTerm ? 'flex-1 min-w-[260px]' : 'w-11'
            }`}
            data-testid="search-wrapper"
          >
            <button
              type="button"
              onClick={() => {
                setSearchExpanded(true);
                setTimeout(() => searchInputRef.current?.focus(), 50);
              }}
              className="absolute left-0 top-0 h-11 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground rounded-lg border border-border bg-background cursor-pointer transition-colors z-10"
              aria-label="Abrir busca"
              data-testid="search-toggle-btn"
            >
              <Search className="w-4 h-4" />
            </button>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar por nº, cliente ou vendedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchExpanded(true)}
              onBlur={() => { if (!searchTerm) setSearchExpanded(false); }}
              className={`h-11 w-full rounded-lg border border-border bg-background pl-12 pr-9 text-sm outline-none transition-opacity duration-200 focus:ring-2 focus:ring-primary/40 ${
                searchExpanded || searchTerm ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              data-testid="search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => { setSearchTerm(''); searchInputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted z-10"
                aria-label="Limpar busca"
                data-testid="search-clear-btn"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Botão de Filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-primary/10 border-primary' : ''}`}
            data-testid="filter-toggle-btn"
          >
            <Filter className="w-4 h-4" />
            Filtros
            {(filters.dataInicio || filters.dataFim || filters.vendedor || filters.pagamento) && (
              <span className="bg-primary text-white text-xs px-1.5 py-0.5 rounded-full">
                {[filters.dataInicio, filters.dataFim, filters.vendedor, filters.pagamento].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Limpar Filtros */}
          {(searchTerm || filters.dataInicio || filters.dataFim || filters.vendedor || filters.pagamento) && (
            <button
              onClick={clearFilters}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {/* LINHA 2: Painel de Filtros (sempre ABAIXO da barra de busca) */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-3 border-t border-border animate-in slide-in-from-top-2" data-testid="filters-panel">
            {/* Filtro por Data */}
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Data Início</label>
              <input
                type="date"
                value={filters.dataInicio}
                onChange={(e) => setFilters(prev => ({ ...prev, dataInicio: e.target.value }))}
                className="input-field text-sm"
                data-testid="filter-data-inicio"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Data Fim</label>
              <input
                type="date"
                value={filters.dataFim}
                onChange={(e) => setFilters(prev => ({ ...prev, dataFim: e.target.value }))}
                className="input-field text-sm"
                data-testid="filter-data-fim"
              />
            </div>

            {/* Filtro por Vendedor */}
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Vendedor</label>
              <select
                value={filters.vendedor}
                onChange={(e) => setFilters(prev => ({ ...prev, vendedor: e.target.value }))}
                className="input-field text-sm"
                data-testid="filter-vendedor"
              >
                <option value="">Todos</option>
                {uniqueVendedores.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            
            {/* Filtro por Pagamento */}
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Forma de Pagamento</label>
              <select
                value={filters.pagamento}
                onChange={(e) => setFilters(prev => ({ ...prev, pagamento: e.target.value }))}
                className="input-field text-sm"
                data-testid="filter-pagamento"
              >
                <option value="">Todas</option>
                {uniqueFormasPagamento.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Barra de Ações em Massa */}
      {selectedIds.length > 0 && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3 animate-in slide-in-from-top-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary">
              {selectedIds.length} item(s) selecionado(s)
            </span>
            <button
              onClick={clearSelection}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Limpar seleção
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* 2 Vias - só aparece com exatamente 1 selecionado */}
            {selectedIds.length === 1 && (
              <button
                onClick={() => {
                  const romaneio = romaneios.find(r => r.id === selectedIds[0]);
                  if (romaneio) handlePrint2Vias(romaneio);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                title="Imprimir 2 vias do mesmo romaneio em 1 folha"
                data-testid="print-2vias-btn"
              >
                <Copy className="w-4 h-4" />
                2 Vias (1 folha)
              </button>
            )}
            {/* Imprimir Juntos - só aparece com exatamente 2 selecionados */}
            {selectedIds.length === 2 && (
              <button
                onClick={handlePrintDuplo}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                title="Imprimir 2 romaneios em 1 folha"
                data-testid="print-duplo-btn"
              >
                <FileText className="w-4 h-4" />
                Imprimir Juntos (1 folha)
              </button>
            )}
            {/* Impressão Inteligente - aparece com 2+ romaneios */}
            {selectedIds.length >= 2 && (
              <button
                onClick={handlePrintInteligente}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                title="Calcular melhor forma de imprimir"
                data-testid="print-inteligente-btn"
                disabled={loadingPrintPreview}
              >
                {loadingPrintPreview ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                Impressão Inteligente
              </button>
            )}
            {/* Edição em Massa */}
            <button
              onClick={() => setShowBulkEditModal(true)}
              className="btn-secondary flex items-center gap-2 text-sm py-2"
              title="Editar selecionados"
              data-testid="bulk-edit-btn"
            >
              <Edit className="w-4 h-4" />
              Editar em Massa
            </button>
            <button
              onClick={handleBulkPrint}
              className="btn-secondary flex items-center gap-2 text-sm py-2"
              title="Imprimir selecionados"
            >
              <Printer className="w-4 h-4" />
              Imprimir
            </button>
            <button
              onClick={handleBulkDownload}
              className="btn-secondary flex items-center gap-2 text-sm py-2"
              title="Baixar PDFs"
            >
              <Download className="w-4 h-4" />
              Baixar PDF
            </button>
            <button
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
              title="Excluir selecionados"
            >
              <Trash2 className="w-4 h-4" />
              Excluir
            </button>
          </div>
        </div>
      )}

      {/* Cards de Resumo (topo) */}
      {(() => {
        const all = romaneios || [];
        const total = all.reduce((s, r) => s + (r.valor_total || 0), 0);
        const totalPrazo = all
          .filter(r => r.forma_pagamento === 'prazo' || r.forma_pagamento === 'a_receber' || r.forma_pagamento === 'boleto')
          .reduce((s, r) => s + (r.valor_total || 0), 0);
        const totalPago = all
          .filter(r => r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago')
          .reduce((s, r) => s + (r.valor_total || 0), 0);
        const totalPendente = total - totalPago;
        // Top clientes
        const porCliente = {};
        all.forEach(r => {
          const k = r.cliente_nome || '?';
          porCliente[k] = (porCliente[k] || 0) + (r.valor_total || 0);
        });
        const topClientes = Object.entries(porCliente)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3);
        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="romaneios-overview-cards">
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground leading-tight">Total Romaneios</p>
                  <p className="text-base font-bold leading-tight truncate">{formatCurrency(total)}</p>
                  <p className="text-[10px] text-muted-foreground">{all.length} pedidos</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground leading-tight">Já Pago</p>
                  <p className="text-base font-bold text-green-600 leading-tight truncate">{formatCurrency(totalPago)}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground leading-tight">A Receber</p>
                  <p className="text-base font-bold text-red-600 leading-tight truncate">{formatCurrency(totalPendente)}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-orange-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground leading-tight">A Prazo / Boleto</p>
                  <p className="text-base font-bold text-orange-600 leading-tight truncate">{formatCurrency(totalPrazo)}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-purple-300 dark:border-purple-700 rounded-lg p-3 col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-purple-600" />
                </div>
                <p className="text-[11px] text-muted-foreground font-semibold">Top 3 Clientes</p>
              </div>
              <div className="space-y-0.5">
                {topClientes.length === 0 ? <p className="text-xs text-muted-foreground">—</p> :
                topClientes.map(([nome, valor], i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="truncate font-medium" title={nome}>{i + 1}. {nome}</span>
                    <span className="text-purple-700 font-bold whitespace-nowrap">{formatCurrency(valor)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Lista de Romaneios */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="romaneios-table">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold w-12">
                  <button
                    onClick={toggleSelectAll}
                    className="p-1 hover:bg-accent rounded transition-colors"
                    title={selectAll ? "Desmarcar todos" : "Selecionar todos"}
                  >
                    {selectAll ? (
                      <CheckSquare className="w-5 h-5 text-primary" />
                    ) : (
                      <Square className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                </th>
                <SortableHeader label="Nº Pedido" sortKey="numero_pedido" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Data" sortKey="data" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Cliente" sortKey="cliente_nome" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Vendedor" sortKey="vendedor_nome" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Valor Total" sortKey="valor_total" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Pagamento" sortKey="forma_pagamento" className="text-center" sortConfig={sortConfig} onSort={handleSort} />
                <th className="px-4 py-3 text-center text-sm font-semibold">Vencimento</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginatedRomaneios.map((romaneio) => (
                <tr 
                  key={romaneio.id} 
                  className={`hover:bg-accent/50 transition-colors ${selectedIds.includes(romaneio.id) ? 'bg-primary/5' : ''}`}
                >
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => toggleSelectOne(romaneio.id)}
                      className="p-1 hover:bg-accent rounded transition-colors"
                    >
                      {selectedIds.includes(romaneio.id) ? (
                        <CheckSquare className="w-5 h-5 text-primary" />
                      ) : (
                        <Square className="w-5 h-5 text-muted-foreground" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{romaneio.numero_pedido || '-'}</span>
                      {romaneio.is_orcamento && (
                        <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded text-[9px] font-bold uppercase tracking-wide" data-testid={`badge-orcamento-${romaneio.id}`}>
                          📋 Orçamento
                        </span>
                      )}
                      {romaneio.origem === 'vendedor' && (
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-[9px] font-bold uppercase tracking-wide" title={`Submetido por ${romaneio.vendedor_nome}`} data-testid={`badge-vendedor-${romaneio.id}`}>
                          👤 Do Vendedor
                        </span>
                      )}
                      {romaneio.origem === 'portal_vendedor' && (
                        <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-[9px] font-bold uppercase tracking-wide" title={`Gerado pelo vendedor ${romaneio.pedido_gerado_por?.nome || romaneio.vendedor_nome}`} data-testid={`badge-portal-vendedor-${romaneio.id}`}>
                          🧾 Vendedor · {romaneio.pedido_gerado_por?.nome || romaneio.vendedor_nome || '?'}
                        </span>
                      )}
                      {romaneio.origem === 'portal_cliente' && (
                        <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded text-[9px] font-bold uppercase tracking-wide" title={`Gerado pelo cliente ${romaneio.pedido_gerado_por?.nome || romaneio.cliente_nome}`} data-testid={`badge-portal-cliente-${romaneio.id}`}>
                          🛒 Cliente · {romaneio.pedido_gerado_por?.nome || romaneio.cliente_nome || '?'}
                        </span>
                      )}
                      {romaneio.pendente_validacao_admin && (
                        <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-[9px] font-bold uppercase tracking-wide animate-pulse" data-testid={`badge-pendente-${romaneio.id}`}>
                          ⚠️ Validar
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {(romaneio.data_criacao || romaneio.created_at) ? new Date(romaneio.data_criacao || romaneio.created_at).toLocaleDateString('pt-BR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">{romaneio.cliente_nome || '-'}</td>
                  <td className="px-4 py-3 text-sm">{romaneio.vendedor_nome || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-primary" data-testid={`romaneio-valor-total-${romaneio.id}`}>
                    {`R$ ${(romaneio.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      romaneio.forma_pagamento === 'pago' ? 'bg-green-100 text-green-800' :
                      romaneio.forma_pagamento === 'a_receber' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {romaneio.forma_pagamento === 'pago' ? 'Pago' :
                       romaneio.forma_pagamento === 'a_receber' ? 'A Receber' :
                       romaneio.forma_pagamento || 'Indefinido'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    {(() => {
                      // Lista de parcelas (datas preenchidas)
                      const parcelas = (romaneio.parcelas_prazo || []).filter(d => d);
                      if (parcelas.length > 0) {
                        const totalLiquido = romaneio.valor_total || 0;
                        const valorPorParc = parcelas.length > 0 ? totalLiquido / parcelas.length : 0;
                        const hoje = new Date();
                        hoje.setHours(0, 0, 0, 0);
                        return (
                          <div className="flex flex-col gap-0.5 items-center" data-testid={`parcelas-cell-${romaneio.id}`}>
                            {parcelas.map((d, i) => {
                              const dt = new Date(d);
                              const vencida = dt < hoje && !(romaneio.status_pagamento === 'pago');
                              return (
                                <div key={i} className={`flex items-center gap-1 text-[11px] ${vencida ? 'text-red-600' : 'text-orange-600'} font-medium leading-tight`}>
                                  <span className="text-[9px] opacity-70">{i + 1}ª</span>
                                  <span>{dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                                  <span className="bg-orange-100 dark:bg-orange-900/30 px-1 rounded text-[10px] font-bold">R$ {valorPorParc.toFixed(2).replace('.', ',')}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                      // Fallback: data_vencimento_boleto ou dias_para_receber
                      if (romaneio.data_vencimento_boleto) {
                        return (
                          <span className="text-orange-600 font-medium">
                            {new Date(romaneio.data_vencimento_boleto).toLocaleDateString('pt-BR')}
                          </span>
                        );
                      }
                      if (romaneio.dias_para_receber && romaneio.created_at) {
                        const dataBase = new Date(romaneio.created_at);
                        const dias = parseInt(romaneio.dias_para_receber);
                        if (!isNaN(dias)) {
                          dataBase.setDate(dataBase.getDate() + dias);
                          return (
                            <span className="text-orange-600 font-medium">
                              {dataBase.toLocaleDateString('pt-BR')}
                            </span>
                          );
                        }
                      }
                      return '-';
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEditModal(romaneio)} className="p-1.5 hover:bg-accent rounded" title="Editar">
                        <Edit className="w-4 h-4 text-primary" />
                      </button>
                      <button onClick={() => handlePrintPDF(romaneio)} className="p-1.5 hover:bg-accent rounded" title="Imprimir">
                        <Printer className="w-4 h-4 text-green-600" />
                      </button>
                      <button onClick={() => handlePrint2Vias(romaneio)} className="p-1.5 hover:bg-accent rounded" title="2 Vias" data-testid={`print-2vias-${romaneio.id}`}>
                        <Copy className="w-4 h-4 text-purple-600" />
                      </button>
                      <button onClick={() => handleShareWhatsApp(romaneio)} className="p-1.5 hover:bg-accent rounded" title="WhatsApp">
                        <Send className="w-4 h-4 text-green-500" />
                      </button>
                      <button onClick={() => handleShareEmail(romaneio)} data-testid={`email-btn-${romaneio.id}`} className="p-1.5 hover:bg-accent rounded" title="Email">
                        <Mail className="w-4 h-4 text-blue-500" />
                      </button>
                      {romaneio.is_orcamento && (
                        <button
                          onClick={() => handleConverterOrcamento(romaneio)}
                          className="p-1.5 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded"
                          title="Converter Orçamento em Romaneio"
                          data-testid={`btn-converter-orcamento-${romaneio.id}`}
                        >
                          <Check className="w-4 h-4 text-amber-600" />
                        </button>
                      )}
                      {romaneio.forma_pagamento !== 'pago' && romaneio.status_pagamento !== 'pago' && !romaneio.is_orcamento && (
                        <button
                          onClick={() => openQuickPay(romaneio)}
                          data-testid={`btn-quick-pay-${romaneio.id}`}
                          className="p-1.5 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                          title="Registrar pagamento"
                        >
                          <DollarSign className="w-4 h-4 text-green-600" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(romaneio.id)} className="p-1.5 hover:bg-accent rounded" title="Deletar">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedRomaneios.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-12 h-12 opacity-30" />
                      <p>Nenhum romaneio cadastrado</p>
                      <button onClick={openNewModal} className="btn-primary mt-2 flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Criar primeiro romaneio
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-muted/50 border-t border-border flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded hover:bg-accent disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded hover:bg-accent disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted">
              <h3 className="text-xl font-bold">
                {editingRomaneio ? `Editar Romaneio #${editingRomaneio.numero_pedido}` : 'Novo Romaneio'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-accent rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6">
              {/* Banner de pendente de validação (vindo do vendedor) */}
              {editingRomaneio?.pendente_validacao_admin && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4 flex items-center gap-3" data-testid="banner-pendente-validacao">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white flex-shrink-0 text-lg">⚠️</div>
                  <div className="flex-1">
                    <p className="font-bold text-amber-900 dark:text-amber-200 text-sm">
                      Romaneio submetido pelo vendedor <b>{editingRomaneio.vendedor_nome}</b>
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                      Confira preços, valores, prazos e forma de pagamento. Depois clique em &ldquo;Marcar como Validado&rdquo;.
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Confirmar que este romaneio foi validado?')) return;
                      try {
                        await axios.post(`${API}/romaneios/${editingRomaneio.id}/validar-pendente`);
                        toast.success('Romaneio validado!');
                        setShowModal(false);
                        fetchAllData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Erro ao validar');
                      }
                    }}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold rounded-lg flex items-center gap-1.5 shadow flex-shrink-0"
                    data-testid="btn-marcar-validado"
                  >
                    <Check className="w-4 h-4" /> Marcar como Validado
                  </button>
                </div>
              )}

              {/* Dados Básicos */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nº Pedido</label>
                  <input
                    type="text"
                    value={formData.numero_pedido}
                    onChange={(e) => setFormData(prev => ({ ...prev, numero_pedido: e.target.value }))}
                    className="input-field"
                    placeholder="Ex: 0001"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Data</label>
                  <input
                    type="date"
                    value={formData.data_romaneio}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_romaneio: e.target.value }))}
                    className="input-field"
                  />
                </div>
                
                {/* Cliente com Autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium mb-1">Cliente</label>
                  <input
                    type="text"
                    value={formData.cliente_nome}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, cliente_nome: e.target.value, cliente_id: '' }));
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    className="input-field"
                    placeholder="Digite o cliente..."
                  />
                  {showClientDropdown && formData.cliente_nome && (
                    <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {clients
                        .filter(c => c.nome?.toLowerCase().includes(formData.cliente_nome.toLowerCase()))
                        .slice(0, 10)
                        .map(c => (
                          <div
                            key={c.id}
                            className="px-3 py-2 hover:bg-accent cursor-pointer text-sm"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, cliente_nome: c.nome, cliente_id: c.id }));
                              setShowClientDropdown(false);
                            }}
                          >
                            {c.nome}
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Vendedor */}
                <div className="relative">
                  <label className="block text-sm font-medium mb-1">Vendedor</label>
                  <input
                    type="text"
                    value={formData.vendedor_nome}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, vendedor_nome: e.target.value, vendedor_id: '' }));
                      setShowSellerDropdown(true);
                    }}
                    onFocus={() => setShowSellerDropdown(true)}
                    className="input-field"
                    placeholder="Digite o vendedor..."
                  />
                  {showSellerDropdown && formData.vendedor_nome && (
                    <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {sellers
                        .filter(s => s.nome?.toLowerCase().includes(formData.vendedor_nome.toLowerCase()))
                        .slice(0, 10)
                        .map(s => (
                          <div
                            key={s.id}
                            className="px-3 py-2 hover:bg-accent cursor-pointer text-sm"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, vendedor_nome: s.nome, vendedor_id: s.id }));
                              setShowSellerDropdown(false);
                            }}
                          >
                            {s.nome}
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Transportadora */}
                <div className="relative">
                  <label className="block text-sm font-medium mb-1">Transportadora</label>
                  <input
                    type="text"
                    value={formData.transportadora_nome}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, transportadora_nome: e.target.value, transportadora_id: '' }));
                      setShowCarrierDropdown(true);
                    }}
                    onFocus={() => setShowCarrierDropdown(true)}
                    className="input-field"
                    placeholder="Digite a transportadora..."
                  />
                  {showCarrierDropdown && formData.transportadora_nome && (
                    <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {carriers
                        .filter(c => c.nome?.toLowerCase().includes(formData.transportadora_nome.toLowerCase()))
                        .slice(0, 10)
                        .map(c => (
                          <div
                            key={c.id}
                            className="px-3 py-2 hover:bg-accent cursor-pointer text-sm"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, transportadora_nome: c.nome, transportadora_id: c.id }));
                              setShowCarrierDropdown(false);
                            }}
                          >
                            {c.nome}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Adicionar Produto */}
              <div className="border border-border rounded-lg p-4 bg-muted/30">
                <h4 className="font-semibold mb-3">Adicionar Produto</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                  {/* Produto */}
                  <div className="md:col-span-2 relative">
                    <label className="block text-xs font-medium mb-1">Produto</label>
                    <input
                      type="text"
                      value={newItem.produto_nome}
                      onChange={(e) => {
                        setNewItem(prev => ({ ...prev, produto_nome: e.target.value, produto_id: '' }));
                        setShowProductDropdown(true);
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                      className="input-field text-sm"
                      placeholder="Buscar produto..."
                    />
                    {showProductDropdown && (
                      <div className="absolute z-[60] w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden" style={{maxHeight: '400px'}}>
                        <div className="overflow-y-auto" style={{maxHeight: '400px'}}>
                          {products
                            .filter(p => 
                              !newItem.produto_nome ||
                              p.produto?.toLowerCase().includes(newItem.produto_nome.toLowerCase()) ||
                              p.codigo?.toLowerCase().includes(newItem.produto_nome.toLowerCase())
                            )
                            .map(p => (
                              <div
                                key={p.id}
                                className="px-3 py-2 hover:bg-accent cursor-pointer text-sm border-b border-border/50 last:border-0"
                                onClick={() => selectProduct(p)}
                              >
                                <div className="font-medium">{p.codigo} - {p.produto}</div>
                                <div className="text-xs text-muted-foreground flex flex-wrap gap-2">
                                  {(p.valor_por_1000 || p.valor_mil) > 0 && (
                                    <span className="bg-primary/10 text-primary px-1 rounded">Mil: R$ {(p.valor_por_1000 || p.valor_mil || 0).toFixed(2)}</span>
                                  )}
                                  {p.valor_por_100 > 0 && (
                                    <span className="bg-blue-100 text-blue-700 px-1 rounded">100: R$ {(p.valor_por_100 || 0).toFixed(2)}</span>
                                  )}
                                  {p.valor_por_unidade > 0 && (
                                    <span className="bg-green-100 text-green-700 px-1 rounded">Un: R$ {(p.valor_por_unidade || 0).toFixed(2)}</span>
                                  )}
                                  {(p.valor_caixa > 0 || p.valor_por_caixa > 0) && (
                                    <span className="bg-orange-100 text-orange-700 px-1 rounded">Cx: R$ {(p.valor_caixa || p.valor_por_caixa || 0).toFixed(2)}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          {products.filter(p => 
                            !newItem.produto_nome ||
                            p.produto?.toLowerCase().includes(newItem.produto_nome.toLowerCase()) ||
                            p.codigo?.toLowerCase().includes(newItem.produto_nome.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              Nenhum produto encontrado
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quantidade */}
                  <div>
                    <label className="block text-xs font-medium mb-1">Qtd</label>
                    <input
                      type="number"
                      value={newItem.quantidade}
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantidade: e.target.value }))}
                      className="input-field text-sm"
                      min="1"
                    />
                  </div>

                  {/* Tipo */}
                  <div>
                    <label className="block text-xs font-medium mb-1">Tipo</label>
                    <select
                      value={newItem.tipo}
                      onChange={(e) => handleTipoChange(e.target.value)}
                      className="input-field text-sm"
                      data-testid="item-tipo-select"
                    >
                      <option value="unidade">Unidade</option>
                      <option value="caixa">Caixa</option>
                      <option value="pacote">Pacote</option>
                      <option value="cento">Cento</option>
                      <option value="milheiro">Milheiro</option>
                    </select>
                  </div>

                  {/* Preço */}
                  <div>
                    <label className="block text-xs font-medium mb-1">Preço (R$)</label>
                    <input
                      type="number"
                      value={newItem.preco_unitario}
                      onChange={(e) => setNewItem(prev => ({ ...prev, preco_unitario: e.target.value }))}
                      className="input-field text-sm"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  {/* % Desconto */}
                  <div>
                    <label className="block text-xs font-medium mb-1">% Desc.</label>
                    <input
                      type="number"
                      value={newItem.percentual_desconto}
                      onChange={(e) => setNewItem(prev => ({ ...prev, percentual_desconto: e.target.value }))}
                      className="input-field text-sm"
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="0"
                    />
                  </div>

                  {/* % Comissão Vendedor */}
                  <div>
                    <label className="block text-xs font-medium mb-1">% Comis.</label>
                    <input
                      type="number"
                      value={newItem.percentual_comissao}
                      onChange={(e) => setNewItem(prev => ({ ...prev, percentual_comissao: e.target.value }))}
                      className="input-field text-sm"
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="0"
                    />
                  </div>

                  {/* Botão Adicionar */}
                  <div className="flex items-end">
                    <button onClick={addItem} className="btn-primary w-full h-10 text-sm flex items-center justify-center gap-1">
                      <Plus className="w-4 h-4" />
                      <span>Add</span>
                    </button>
                  </div>
                </div>

                {/* Preview do valor com desconto */}
                {newItem.quantidade && newItem.preco_unitario && (
                  <div className="mt-2 text-sm text-right flex items-center justify-end gap-4">
                    {parseFloat(newItem.percentual_desconto) > 0 && (
                      <span className="text-green-600">
                        <span className="text-muted-foreground">Desconto: </span>
                        {newItem.percentual_desconto}%
                      </span>
                    )}
                    {parseFloat(newItem.percentual_comissao) > 0 && (
                      <span className="text-blue-600">
                        <span className="text-muted-foreground">Comissão: </span>
                        {newItem.percentual_comissao}%
                        <span className="text-xs text-muted-foreground ml-1">(info)</span>
                      </span>
                    )}
                    <span>
                      <span className="text-muted-foreground">Total: </span>
                      <span className="font-bold text-primary">
                        R$ {(() => {
                          const preco = parseFloat(newItem.preco_unitario) || 0;
                          const desc = parseFloat(newItem.percentual_desconto) || 0;
                          // Comissão NÃO desconta do valor - apenas informação
                          const precoFinal = preco * (1 - desc / 100);
                          return calcularValorItem(newItem.quantidade, precoFinal.toFixed(2));
                        })()}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {/* Lista de Itens */}
              {formData.items.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left">Produto</th>
                        <th className="px-3 py-2 text-center">Qtd</th>
                        <th className="px-3 py-2 text-center">Tipo</th>
                        <th className="px-3 py-2 text-right">Preço</th>
                        <th className="px-3 py-2 text-center">% Desc.</th>
                        <th className="px-3 py-2 text-center">% Com.</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {formData.items.map((item, index) => (
                        <tr key={index} className="hover:bg-accent/30">
                          <td className="px-3 py-2 font-medium">{item.produto_nome}</td>
                          <td className="px-3 py-2 text-center">{item.quantidade}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                              {item.tipo_entrada || 'mil'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">R$ {item.valor_unitario?.toFixed(2)}</td>
                          <td className="px-3 py-2 text-center">
                            {item.percentual_desconto > 0 ? (
                              <span className="text-green-600 font-medium">{item.percentual_desconto}%</span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {item.percentual_comissao > 0 ? (
                              <span className="text-blue-600 font-medium">{item.percentual_comissao}%</span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-primary">R$ {item.valor_total?.toFixed(2)}</td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button 
                                onClick={() => editItem(index)} 
                                className="text-blue-600 hover:text-blue-800"
                                title="Editar item"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => removeItem(index)} 
                                className="text-red-600 hover:text-red-800"
                                title="Remover item"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Totais e Pagamento */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Pagamento */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Pagamento</h4>
                    {/* Toggle Orçamento */}
                    <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-dashed cursor-pointer transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/20"
                      style={{ borderColor: formData.is_orcamento ? '#f59e0b' : '#cbd5e1' }}
                      data-testid="toggle-orcamento">
                      <input
                        type="checkbox"
                        checked={!!formData.is_orcamento}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_orcamento: e.target.checked }))}
                        className="accent-amber-500"
                      />
                      <span className={`text-xs font-bold ${formData.is_orcamento ? 'text-amber-600' : 'text-gray-500'}`}>
                        {formData.is_orcamento ? '📋 SALVAR COMO ORÇAMENTO' : 'Salvar como Orçamento'}
                      </span>
                    </label>
                  </div>
                  {formData.is_orcamento && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2 text-[11px] text-amber-800 dark:text-amber-300">
                      ℹ️ Este registro entrará como <b>Orçamento</b>: não conta no faturamento e fica numa lista separada. Você pode convertê-lo em Romaneio depois.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Forma de Pagamento</label>
                      <select
                        value={formData.forma_pagamento}
                        onChange={(e) => setFormData(prev => ({ ...prev, forma_pagamento: e.target.value }))}
                        className="input-field text-sm"
                        data-testid="forma-pagamento-select"
                      >
                        <option value="a_receber">A Receber</option>
                        <option value="pago">Pago</option>
                        <option value="avista">À Vista</option>
                        <option value="boleto">Boleto</option>
                        <option value="pix">PIX</option>
                        <option value="cartao">Cartão</option>
                        <option value="prazo">A Prazo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Desconto Geral</label>
                      <div className="flex items-center border border-border rounded-lg overflow-hidden bg-background">
                        <select
                          value={formData.desconto_tipo}
                          onChange={(e) => setFormData(prev => ({ ...prev, desconto_tipo: e.target.value }))}
                          className="bg-muted px-2 py-2 text-sm border-r border-border focus:outline-none cursor-pointer"
                        >
                          <option value="valor">R$</option>
                          <option value="percentual">%</option>
                        </select>
                        <input
                          type="number"
                          value={formData.desconto_valor}
                          onChange={(e) => setFormData(prev => ({ ...prev, desconto_valor: e.target.value }))}
                          className="flex-1 px-3 py-2 text-sm focus:outline-none bg-transparent"
                          step="0.01"
                          min="0"
                          placeholder={formData.desconto_tipo === 'percentual' ? '10' : '50.00'}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Campo de dias para receber - aparece sempre exceto quando é boleto ou à vista */}
                  {(formData.forma_pagamento === 'a_receber' || formData.forma_pagamento === 'prazo') && (
                    <div>
                      <label className="block text-xs font-medium mb-1">Dias para Receber / Prazo (opcional)</label>
                      <input
                        type="number"
                        value={formData.dias_para_receber}
                        onChange={(e) => setFormData(prev => ({ ...prev, dias_para_receber: e.target.value }))}
                        className="input-field text-sm"
                        placeholder="Ex: 30"
                        min="0"
                      />
                    </div>
                  )}
                  {/* Campos de parcelas - aparece quando forma_pagamento é prazo */}
                  {formData.forma_pagamento === 'prazo' && (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium mb-1">Datas de Vencimento (opcional)</label>
                      <div className="grid grid-cols-3 gap-2">
                        {(() => {
                          const datasPreenchidas = (formData.parcelas_prazo || []).filter(d => d).length || 6;
                          // Valor por parcela = valor total / nº parcelas preenchidas (mínimo 1)
                          const valorTotalRom = (formData.items || []).reduce((s, it) => s + (it.valor_total || 0), 0);
                          const descontoGeralVal = formData.desconto_tipo === 'percentual'
                            ? valorTotalRom * ((parseFloat(formData.desconto_valor) || 0) / 100)
                            : (parseFloat(formData.desconto_valor) || 0);
                          const totalLiquido = Math.max(0, valorTotalRom - descontoGeralVal);
                          const valorPorParcela = datasPreenchidas > 0 ? totalLiquido / datasPreenchidas : 0;
                          return [0, 1, 2, 3, 4, 5].map((i) => {
                            const dataPreenchida = formData.parcelas_prazo[i];
                            const valor = dataPreenchida ? valorPorParcela : 0;
                            return (
                              <div key={i}>
                                <label className="block text-xs text-muted-foreground mb-1">{i + 1}ª Parcela</label>
                                {dataPreenchida && valor > 0 && (
                                  <div className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-bold rounded px-2 py-1 mb-1 text-center" data-testid={`parcela-valor-${i}`}>
                                    R$ {valor.toFixed(2).replace('.', ',')}
                                  </div>
                                )}
                                <input
                                  type="date"
                                  value={formData.parcelas_prazo[i] || ''}
                                  onChange={(e) => {
                                    const newParcelas = [...formData.parcelas_prazo];
                                    newParcelas[i] = e.target.value;
                                    setFormData(prev => ({ ...prev, parcelas_prazo: newParcelas }));
                                  }}
                                  className="input-field text-sm"
                                />
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                  {/* Boletos detalhados — aparece quando forma_pagamento = boleto */}
                  {formData.forma_pagamento === 'boleto' && (
                    <div className="space-y-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/40 rounded-lg p-3" data-testid="boletos-editor">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-blue-900 dark:text-blue-300 flex items-center gap-1.5">
                          📄 Boletos (Data + Valor)
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const novos = [...(formData.boletos || []), { data_vencimento: '', valor: '', numero: '', descricao: '' }];
                            setFormData(prev => ({ ...prev, boletos: novos }));
                          }}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          data-testid="add-boleto-btn"
                        >
                          + Adicionar Boleto
                        </button>
                      </div>
                      {(formData.boletos || []).length === 0 ? (
                        <p className="text-[11px] text-blue-700 dark:text-blue-300 italic">Nenhum boleto cadastrado ainda. Use &ldquo;Adicionar Boleto&rdquo; para informar a data de vencimento e o valor de cada um.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(formData.boletos || []).map((b, i) => (
                            <div key={i} className="border border-blue-200 dark:border-blue-800/30 rounded-lg p-2 bg-white/50 dark:bg-black/10" data-testid={`boleto-row-${i}`}>
                            <div className="grid grid-cols-12 gap-1.5 items-end">
                              <div className="col-span-3">
                                <label className="text-[10px] text-muted-foreground">Vencimento</label>
                                <input
                                  type="date"
                                  value={b.data_vencimento || ''}
                                  onChange={(e) => {
                                    const novos = [...formData.boletos];
                                    novos[i] = { ...novos[i], data_vencimento: e.target.value };
                                    setFormData(prev => ({ ...prev, boletos: novos }));
                                  }}
                                  className="input-field text-xs h-8"
                                  data-testid={`boleto-data-${i}`}
                                />
                              </div>
                              <div className="col-span-3">
                                <label className="text-[10px] text-muted-foreground">Valor R$</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={b.valor || ''}
                                  onChange={(e) => {
                                    const novos = [...formData.boletos];
                                    novos[i] = { ...novos[i], valor: parseFloat(e.target.value) || 0 };
                                    setFormData(prev => ({ ...prev, boletos: novos }));
                                  }}
                                  className="input-field text-xs h-8"
                                  data-testid={`boleto-valor-${i}`}
                                />
                              </div>
                              <div className="col-span-3">
                                <label className="text-[10px] text-muted-foreground">Nº / Descrição</label>
                                <input
                                  type="text"
                                  value={b.numero || ''}
                                  onChange={(e) => {
                                    const novos = [...formData.boletos];
                                    novos[i] = { ...novos[i], numero: e.target.value };
                                    setFormData(prev => ({ ...prev, boletos: novos }));
                                  }}
                                  placeholder="Ex: 1/3"
                                  className="input-field text-xs h-8"
                                  data-testid={`boleto-numero-${i}`}
                                />
                              </div>
                              <div className="col-span-3 flex justify-end items-center gap-1 pb-0.5">
                                {/* Status pago/pendente */}
                                {editingRomaneio && b.id && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const novoStatus = (b.status === 'pago') ? 'pendente' : 'pago';
                                      try {
                                        const res = await axios.patch(`${API}/romaneios/${editingRomaneio.id}/boletos/${b.id}`, { status: novoStatus });
                                        const novos = [...formData.boletos];
                                        novos[i] = { ...novos[i], status: novoStatus, data_pagamento: novoStatus === 'pago' ? new Date().toISOString().split('T')[0] : null };
                                        setFormData(prev => ({ ...prev, boletos: novos, status_pagamento: res.data?.status_pagamento || prev.status_pagamento }));
                                        toast.success(novoStatus === 'pago' ? 'Boleto marcado como pago' : 'Boleto marcado como pendente');
                                      } catch (e) { toast.error('Erro ao atualizar'); }
                                    }}
                                    className={`text-[10px] px-2 py-1 rounded-full font-bold transition ${
                                      b.status === 'pago'
                                        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                        : 'bg-gray-200 text-gray-600 hover:bg-emerald-100 hover:text-emerald-700'
                                    }`}
                                    title={b.status === 'pago' ? 'Clique para marcar como pendente' : 'Clique para marcar como PAGO'}
                                    data-testid={`boleto-toggle-pago-${i}`}
                                  >
                                    {b.status === 'pago' ? '✓ PAGO' : 'A PAGAR'}
                                  </button>
                                )}
                                {editingRomaneio && b.id && (
                                  <ComprovanteCell
                                    romaneioId={editingRomaneio.id}
                                    paymentType="boleto"
                                    paymentIndex={i}
                                    canUpload={true}
                                    canDelete={true}
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const novos = formData.boletos.filter((_, j) => j !== i);
                                    setFormData(prev => ({ ...prev, boletos: novos }));
                                  }}
                                  className="text-red-500 hover:text-red-700 p-1.5"
                                  title="Remover"
                                  data-testid={`boleto-remove-${i}`}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                            {/* Comprovantes anexados a esta parcela */}
                            {editingRomaneio && b.id && (
                              <ComprovantesBoletoList
                                romaneioId={editingRomaneio.id}
                                boletoId={b.id}
                                boletoNumero={b.numero || `Parcela ${i + 1}`}
                              />
                            )}
                            </div>
                          ))}
                          {(() => {
                            const totalBoletos = (formData.boletos || []).reduce((s, b) => s + (parseFloat(b.valor) || 0), 0);
                            return (
                              <div className="text-[11px] text-blue-900 dark:text-blue-300 font-semibold pt-1 border-t border-blue-200 dark:border-blue-800/40">
                                Total dos boletos: <span className="text-blue-700 dark:text-blue-200">R$ {totalBoletos.toFixed(2)}</span>
                              </div>
                            );
                          })()}
                          <p className="text-[10px] text-blue-700 dark:text-blue-300/80">
                            💡 Após salvar o romaneio, você poderá anexar o PDF de cada boleto na tela de detalhes.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium mb-1">Número da Nota Fiscal</label>
                    <input
                      type="text"
                      value={formData.numero_nota_fiscal}
                      onChange={(e) => setFormData(prev => ({ ...prev, numero_nota_fiscal: e.target.value }))}
                      className="input-field text-sm"
                      placeholder="Ex: 12345"
                      data-testid="numero-nota-fiscal-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 flex items-center gap-1.5">
                      Pagamento Antecipado (R$)
                      <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">opcional</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.pagamento_antecipado}
                      onChange={(e) => setFormData(prev => ({ ...prev, pagamento_antecipado: e.target.value }))}
                      className="input-field text-sm"
                      placeholder="Ex: 200.00"
                      data-testid="pagamento-antecipado-input"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">Valor que o cliente adiantou. Será abatido do total.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Observações</label>
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                      className="input-field text-sm h-20 resize-none"
                      placeholder="Observações do pedido..."
                    />
                  </div>
                </div>

                {/* Resumo */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Resumo do Pedido</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal ({formData.items.length} itens):</span>
                      <span>R$ {formData.items.reduce((sum, i) => sum + (i.valor_total || 0), 0).toFixed(2)}</span>
                    </div>
                    {/* Mostrar desconto por item se houver */}
                    {formData.items.some(i => i.percentual_desconto > 0) && (
                      <div className="flex justify-between text-green-600 text-xs">
                        <span>Descontos aplicados:</span>
                        <span>
                          - R$ {formData.items.reduce((sum, i) => {
                            // Apenas desconto reduz o valor - comissão é apenas informação
                            const descItemValor = i.valor_unitario * i.quantidade * ((i.percentual_desconto || 0) / 100);
                            return sum + descItemValor;
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {/* Mostrar comissões como informação */}
                    {formData.items.some(i => i.percentual_comissao > 0) && (
                      <div className="flex justify-between text-blue-600 text-xs">
                        <span>Comissões (info vendedor):</span>
                        <span>
                          R$ {formData.items.reduce((sum, i) => {
                            // Comissão é calculada sobre o valor com desconto
                            const valorComDesconto = i.valor_unitario * i.quantidade * (1 - (i.percentual_desconto || 0) / 100);
                            const comissaoValor = valorComDesconto * ((i.percentual_comissao || 0) / 100);
                            return sum + comissaoValor;
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {parseFloat(formData.desconto_valor) > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Desconto geral {formData.desconto_tipo === 'percentual' ? `(${formData.desconto_valor}%)` : ''}:</span>
                        <span>- R$ {calcularDesconto().toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(formData.pagamento_antecipado) > 0 && (
                      <div className="flex justify-between text-blue-600">
                        <span>Pagamento antecipado:</span>
                        <span>- R$ {parseFloat(formData.pagamento_antecipado).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-border font-bold text-lg">
                      <span>TOTAL:</span>
                      <span className="text-primary">R$ {calcularTotal()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted">
              <button onClick={() => setShowModal(false)} className="btn-secondary">
                Cancelar
              </button>
              <button onClick={handleSubmit} className="btn-primary">
                {editingRomaneio ? 'Atualizar' : 'Salvar'} Romaneio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(showClientDropdown || showSellerDropdown || showCarrierDropdown || showProductDropdown) && (
        <div 
          className="fixed inset-0 z-[45]" 
          onClick={() => {
            setShowClientDropdown(false);
            setShowSellerDropdown(false);
            setShowCarrierDropdown(false);
            setShowProductDropdown(false);
          }} 
        />
      )}

      {/* Modal de Edição em Massa */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-edit-modal">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md animate-in zoom-in-95">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold">Editar em Massa</h3>
              <button 
                onClick={() => setShowBulkEditModal(false)}
                className="p-1 hover:bg-accent rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Editando {selectedIds.length} romaneio(s) selecionado(s)
              </p>
              
              <div>
                <label className="block text-sm font-medium mb-2">Campo a alterar</label>
                <select
                  value={bulkEditField}
                  onChange={(e) => {
                    setBulkEditField(e.target.value);
                    setBulkEditValue('');
                  }}
                  className="input-field"
                  data-testid="bulk-edit-field-select"
                >
                  <option value="">Selecione...</option>
                  <option value="vendedor">Vendedor</option>
                  <option value="forma_pagamento">Forma de Pagamento</option>
                  <option value="status_pagamento">Status de Pagamento</option>
                </select>
              </div>
              
              {bulkEditField === 'vendedor' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Novo Vendedor</label>
                  <select
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    className="input-field"
                    data-testid="bulk-edit-vendedor-select"
                  >
                    <option value="">Selecione...</option>
                    {sellers.map(s => (
                      <option key={s.id} value={s.id}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {bulkEditField === 'forma_pagamento' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Nova Forma de Pagamento</label>
                  <select
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    className="input-field"
                    data-testid="bulk-edit-pagamento-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="a_receber">A Receber</option>
                    <option value="pago">Pago</option>
                    <option value="avista">À Vista</option>
                    <option value="boleto">Boleto</option>
                    <option value="pix">PIX</option>
                    <option value="cartao">Cartão</option>
                    <option value="prazo">A Prazo</option>
                  </select>
                </div>
              )}
              
              {bulkEditField === 'status_pagamento' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Novo Status</label>
                  <select
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    className="input-field"
                    data-testid="bulk-edit-status-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="pendente">Pendente</option>
                    <option value="pago">Pago</option>
                    <option value="atrasado">Atrasado</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted">
              <button 
                onClick={() => setShowBulkEditModal(false)} 
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button 
                onClick={handleBulkEdit} 
                className="btn-primary"
                disabled={!bulkEditField || !bulkEditValue}
                data-testid="bulk-edit-confirm-btn"
              >
                Aplicar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Preview do Upload */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="preview-modal">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl animate-in zoom-in-95 my-8">
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card z-10">
              <div>
                <h3 className="text-lg font-semibold">Verificar Romaneio Importado</h3>
                <p className="text-sm text-muted-foreground">Revise os dados extraídos da imagem antes de salvar</p>
              </div>
              <button 
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewData(null);
                  setUploadedImageUrl('');
                }}
                className="p-1 hover:bg-accent rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Grid com imagem e dados */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Imagem Original */}
                {uploadedImageUrl && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Imagem Original</label>
                    <div className="border border-border rounded-lg overflow-hidden bg-muted">
                      <img 
                        src={uploadedImageUrl} 
                        alt="Romaneio" 
                        className="w-full h-auto max-h-[400px] object-contain"
                      />
                    </div>
                  </div>
                )}
                
                {/* Dados Extraídos */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Nº Pedido</label>
                      <input
                        type="text"
                        value={previewData.numero_pedido || ''}
                        onChange={(e) => setPreviewData(prev => ({ ...prev, numero_pedido: e.target.value }))}
                        className="input-field text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Data</label>
                      <input
                        type="date"
                        value={previewData.data_romaneio || ''}
                        onChange={(e) => setPreviewData(prev => ({ ...prev, data_romaneio: e.target.value }))}
                        className="input-field text-sm"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium mb-1">Cliente</label>
                    <select
                      value={previewData.cliente_id || ''}
                      onChange={(e) => {
                        const client = clients.find(c => c.id === e.target.value);
                        setPreviewData(prev => ({
                          ...prev,
                          cliente_id: e.target.value,
                          cliente_nome: client?.nome || ''
                        }));
                      }}
                      className="input-field text-sm"
                    >
                      <option value="">Selecione o cliente...</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                    {previewData.cliente_nome && !previewData.cliente_id && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Detectado: &ldquo;{previewData.cliente_nome}&rdquo; - selecione o cliente correto
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium mb-1">Vendedor</label>
                    <select
                      value={previewData.vendedor_id || ''}
                      onChange={(e) => {
                        const seller = sellers.find(s => s.id === e.target.value);
                        setPreviewData(prev => ({
                          ...prev,
                          vendedor_id: e.target.value,
                          vendedor_nome: seller?.nome || ''
                        }));
                      }}
                      className="input-field text-sm"
                    >
                      <option value="">Selecione o vendedor...</option>
                      {sellers.map(s => (
                        <option key={s.id} value={s.id}>{s.nome}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium mb-1">Forma de Pagamento</label>
                    <select
                      value={previewData.forma_pagamento || 'a_receber'}
                      onChange={(e) => setPreviewData(prev => ({ ...prev, forma_pagamento: e.target.value }))}
                      className="input-field text-sm"
                    >
                      <option value="a_receber">A Receber</option>
                      <option value="pago">Pago</option>
                      <option value="avista">À Vista</option>
                      <option value="boleto">Boleto</option>
                      <option value="pix">PIX</option>
                      <option value="cartao">Cartão</option>
                      <option value="prazo">A Prazo</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Itens */}
              <div>
                <label className="block text-sm font-medium mb-2">Itens Extraídos</label>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left">Produto</th>
                        <th className="px-3 py-2 text-center w-20">Qtd</th>
                        <th className="px-3 py-2 text-right w-28">Preço</th>
                        <th className="px-3 py-2 text-right w-28">Total</th>
                        <th className="px-3 py-2 w-12"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {previewData.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">
                            <select
                              value={item.produto_id || ''}
                              onChange={(e) => {
                                const prod = products.find(p => p.id === e.target.value);
                                const newItems = [...previewData.items];
                                newItems[idx] = {
                                  ...newItems[idx],
                                  produto_id: e.target.value,
                                  produto_nome: prod?.produto || item.produto_nome,
                                  valor_unitario: prod?.valor_por_1000 || item.valor_unitario
                                };
                                setPreviewData(prev => ({ ...prev, items: newItems }));
                              }}
                              className="input-field text-xs w-full"
                            >
                              <option value="">{item.produto_nome || 'Selecione...'}</option>
                              {products.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.codigo ? `${p.codigo} - ` : ''}{p.produto}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.quantidade || ''}
                              onChange={(e) => {
                                const newItems = [...previewData.items];
                                const qtd = parseFloat(e.target.value) || 0;
                                newItems[idx] = {
                                  ...newItems[idx],
                                  quantidade: qtd,
                                  valor_total: qtd * (newItems[idx].valor_unitario || 0)
                                };
                                setPreviewData(prev => ({ ...prev, items: newItems }));
                              }}
                              className="input-field text-xs w-full text-center"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.valor_unitario || ''}
                              onChange={(e) => {
                                const newItems = [...previewData.items];
                                const preco = parseFloat(e.target.value) || 0;
                                newItems[idx] = {
                                  ...newItems[idx],
                                  valor_unitario: preco,
                                  valor_total: (newItems[idx].quantidade || 0) * preco
                                };
                                setPreviewData(prev => ({ ...prev, items: newItems }));
                              }}
                              className="input-field text-xs w-full text-right"
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            R$ {(item.valor_total || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => {
                                const newItems = previewData.items.filter((_, i) => i !== idx);
                                setPreviewData(prev => ({ ...prev, items: newItems }));
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Total */}
              <div className="flex justify-end">
                <div className="bg-muted rounded-lg p-4 w-64">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Subtotal:</span>
                    <span>R$ {(previewData.items?.reduce((sum, i) => sum + (i.valor_total || 0), 0) || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-primary">
                    <span>Total:</span>
                    <span>R$ {(previewData.items?.reduce((sum, i) => sum + (i.valor_total || 0), 0) || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted sticky bottom-0">
              <button 
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewData(null);
                  setUploadedImageUrl('');
                }} 
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSavePreview} 
                className="btn-primary"
                data-testid="save-preview-btn"
              >
                Salvar Romaneio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Preview da Impressão Inteligente */}
      {showPrintPreview && printPreviewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="print-preview-modal">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-lg animate-in zoom-in-95">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold">Impressão Inteligente</h3>
                <p className="text-sm text-muted-foreground">Cálculo de páginas otimizado</p>
              </div>
              <button 
                onClick={() => setShowPrintPreview(false)}
                className="p-1 hover:bg-accent rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Resumo */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {printPreviewData.total_paginas} folha{printPreviewData.total_paginas !== 1 ? 's' : ''}
                </div>
                <div className="text-sm text-blue-700">
                  para {printPreviewData.total_romaneios} romaneio{printPreviewData.total_romaneios !== 1 ? 's' : ''}
                </div>
                {printPreviewData.economia_papel > 0 && (
                  <div className="mt-2 text-green-600 font-medium flex items-center justify-center gap-1">
                    <span>Economia de {printPreviewData.economia_papel} folha{printPreviewData.economia_papel !== 1 ? 's' : ''}!</span>
                  </div>
                )}
              </div>
              
              {/* Detalhes */}
              <div className="space-y-3">
                {printPreviewData.detalhes?.pequenos?.quantidade > 0 && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">Romaneios pequenos (até 5 itens)</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">2 por página</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {printPreviewData.detalhes.pequenos.quantidade} romaneio(s) → {printPreviewData.detalhes.pequenos.paginas} página(s)
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {printPreviewData.detalhes.pequenos.romaneios?.slice(0, 10).map((r, i) => (
                        <span key={i} className="text-xs bg-background px-1.5 py-0.5 rounded border">{r}</span>
                      ))}
                      {printPreviewData.detalhes.pequenos.romaneios?.length > 10 && (
                        <span className="text-xs text-muted-foreground">+{printPreviewData.detalhes.pequenos.romaneios.length - 10} mais</span>
                      )}
                    </div>
                  </div>
                )}
                
                {printPreviewData.detalhes?.grandes?.quantidade > 0 && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">Romaneios grandes (6+ itens)</span>
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">1 por página</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {printPreviewData.detalhes.grandes.quantidade} romaneio(s) → {printPreviewData.detalhes.grandes.paginas} página(s)
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {printPreviewData.detalhes.grandes.romaneios?.slice(0, 6).map((r, i) => (
                        <span key={i} className="text-xs bg-background px-1.5 py-0.5 rounded border">{r}</span>
                      ))}
                      {printPreviewData.detalhes.grandes.romaneios?.length > 6 && (
                        <span className="text-xs text-muted-foreground">+{printPreviewData.detalhes.grandes.romaneios.length - 6} mais</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Sugestão */}
              <div className="text-center text-sm text-muted-foreground">
                {printPreviewData.sugestao}
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted">
              <button 
                onClick={() => setShowPrintPreview(false)} 
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button 
                onClick={confirmarPrintInteligente} 
                className="btn-primary flex items-center gap-2"
                data-testid="confirm-print-btn"
              >
                <Printer className="w-4 h-4" />
                Gerar PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay para upload */}
      {uploadingImage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 flex flex-col items-center gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <p className="text-sm">Analisando imagem com IA...</p>
          </div>
        </div>
      )}

      {/* Modal de Envio de Email */}
      {emailModal.open && emailModal.romaneio && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !emailModal.sending && setEmailModal(prev => ({ ...prev, open: false }))}
          data-testid="email-modal"
        >
          <div
            className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-semibold">
                  Enviar Romaneio por Email
                </h3>
              </div>
              <button
                onClick={() => !emailModal.sending && setEmailModal(prev => ({ ...prev, open: false }))}
                className="text-muted-foreground hover:text-foreground p-1"
                data-testid="email-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                <div><span className="text-muted-foreground">Romaneio:</span> <strong>#{emailModal.romaneio.numero_pedido}</strong></div>
                <div><span className="text-muted-foreground">Cliente:</span> <strong>{emailModal.romaneio.cliente_nome}</strong></div>
                {emailModal.whatsapp && (
                  <div className="flex items-center gap-2">
                    <MessageCircleIcon />
                    <span className="text-muted-foreground">WhatsApp cadastrado:</span>
                    <span className="font-mono text-green-600">{emailModal.whatsapp}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Para <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={emailModal.to}
                  onChange={(e) => setEmailModal(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="cliente@exemplo.com"
                  className="input-field w-full"
                  data-testid="email-to-input"
                />
                {!emailModal.to && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Cliente sem email cadastrado. Preencha o email de destino.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Assunto</label>
                <input
                  type="text"
                  value={emailModal.subject}
                  onChange={(e) => setEmailModal(prev => ({ ...prev, subject: e.target.value }))}
                  className="input-field w-full"
                  data-testid="email-subject-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Mensagem</label>
                <textarea
                  value={emailModal.message}
                  onChange={(e) => setEmailModal(prev => ({ ...prev, message: e.target.value }))}
                  rows={8}
                  className="input-field w-full font-mono text-sm"
                  data-testid="email-message-input"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={emailModal.attachPdf}
                  onChange={(e) => setEmailModal(prev => ({ ...prev, attachPdf: e.target.checked }))}
                  className="w-4 h-4 rounded"
                  data-testid="email-attach-pdf"
                />
                <span className="text-sm">
                  Anexar PDF do romaneio (recomendado)
                </span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-border bg-muted flex items-center justify-end gap-3">
              <button
                onClick={() => setEmailModal(prev => ({ ...prev, open: false }))}
                disabled={emailModal.sending}
                className="btn-secondary"
                data-testid="email-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailModal.sending || !emailModal.to}
                className="btn-primary flex items-center gap-2"
                data-testid="email-send-btn"
              >
                {emailModal.sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MINI-MODAL PAGAMENTO RÁPIDO */}
      {showQuickPayModal && quickPayRomaneio && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="quick-pay-modal">
          <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-green-50 dark:bg-green-900/20">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold">Registrar Pagamento</h3>
                  <p className="text-xs text-muted-foreground">Romaneio #{quickPayRomaneio.numero_pedido}</p>
                </div>
              </div>
              <button onClick={() => setShowQuickPayModal(false)} className="p-1.5 hover:bg-accent rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="font-bold">{formatCurrency(quickPayRomaneio.valor_total || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Já pago</p>
                  <p className="font-bold text-green-600">{formatCurrency(quickPayRomaneio.valor_pago || 0)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Saldo a pagar</p>
                  <p className="font-bold text-orange-600 text-lg">
                    {formatCurrency(Math.max(0, (quickPayRomaneio.valor_total || 0) - (quickPayRomaneio.valor_pago || 0)))}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Valor a registrar (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={quickPayForm.valor}
                  onChange={(e) => setQuickPayForm(prev => ({ ...prev, valor: e.target.value }))}
                  className="input-field text-sm"
                  data-testid="quick-pay-valor"
                  autoFocus
                />
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setQuickPayForm(prev => ({ ...prev, valor: Math.max(0, (quickPayRomaneio.valor_total || 0) - (quickPayRomaneio.valor_pago || 0)).toFixed(2) }))}
                    className="text-xs px-2 py-1 bg-primary/10 text-primary rounded hover:bg-primary/20 font-medium"
                  >
                    Total (saldo)
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickPayForm(prev => ({ ...prev, valor: (((quickPayRomaneio.valor_total || 0) - (quickPayRomaneio.valor_pago || 0)) / 2).toFixed(2) }))}
                    className="text-xs px-2 py-1 bg-accent rounded hover:bg-accent/80 font-medium"
                  >
                    50%
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Forma</label>
                  <select
                    value={quickPayForm.forma}
                    onChange={(e) => setQuickPayForm(prev => ({ ...prev, forma: e.target.value }))}
                    className="input-field text-sm"
                    data-testid="quick-pay-forma"
                  >
                    <option value="pix">PIX</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="cartao">Cartão</option>
                    <option value="boleto">Boleto</option>
                    <option value="transferencia">Transferência</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Data</label>
                  <input
                    type="date"
                    value={quickPayForm.data}
                    onChange={(e) => setQuickPayForm(prev => ({ ...prev, data: e.target.value }))}
                    className="input-field text-sm"
                    data-testid="quick-pay-data"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Observação (opcional)</label>
                <input
                  type="text"
                  value={quickPayForm.observacao}
                  onChange={(e) => setQuickPayForm(prev => ({ ...prev, observacao: e.target.value }))}
                  className="input-field text-sm"
                  placeholder="Ex: parcela 1 de 3"
                />
              </div>

              {/* Pagar Comissão do Vendedor — sempre exibido */}
              <div className="p-3 rounded-lg border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20" data-testid="quick-pay-pagar-comissao-label">
                {quickPayComissao.vendedor ? (
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="quick-pay-pagar-comissao"
                      checked={quickPayForm.pagar_comissao}
                      disabled={quickPayComissao.pendente <= 0.01}
                      onChange={(e) => setQuickPayForm(prev => ({ ...prev, pagar_comissao: e.target.checked }))}
                      className="mt-1 w-4 h-4 accent-purple-600 disabled:opacity-40"
                      data-testid="quick-pay-pagar-comissao"
                    />
                    <label htmlFor="quick-pay-pagar-comissao" className="flex-1 text-sm cursor-pointer">
                      <p className="font-semibold text-purple-700 dark:text-purple-300">
                        Pagar comissão do vendedor junto
                      </p>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                        <div>
                          <p className="text-muted-foreground">Vendedor</p>
                          <p className="font-bold">{quickPayComissao.vendedor}</p>
                          <p className="text-[10px] text-muted-foreground">{quickPayComissao.percentual}% comissão</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Total comissão</p>
                          <p className="font-bold">{formatCurrency(quickPayComissao.total)}</p>
                          {quickPayComissao.paga > 0 && (
                            <p className="text-[10px] text-green-600">Pago: {formatCurrency(quickPayComissao.paga)}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-muted-foreground">Pendente</p>
                          <p className={`font-bold ${quickPayComissao.pendente > 0.01 ? 'text-purple-700' : 'text-green-600'}`}>
                            {formatCurrency(quickPayComissao.pendente)}
                          </p>
                        </div>
                      </div>
                      {quickPayComissao.pendente <= 0.01 && quickPayComissao.total > 0 && (
                        <p className="text-[11px] text-green-600 mt-2">✓ Comissão deste romaneio já foi quitada</p>
                      )}
                      {quickPayComissao.total === 0 && (
                        <p className="text-[11px] text-muted-foreground mt-2">Romaneio sem valor — não há comissão a pagar</p>
                      )}
                      {quickPayForm.pagar_comissao && quickPayComissao.pendente > 0.01 && (
                        <p className="text-[11px] text-green-600 mt-2">✓ Marcará a comissão como paga na página do vendedor</p>
                      )}
                    </label>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">⚠️ Este romaneio não tem vendedor cadastrado — não há comissão a registrar.</p>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border bg-muted/30 flex justify-end gap-2">
              <button onClick={() => setShowQuickPayModal(false)} className="px-4 py-2 text-sm rounded hover:bg-accent">Cancelar</button>
              <button onClick={submitQuickPay} data-testid="quick-pay-submit" className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
                <Check className="w-4 h-4" /> Registrar Pagamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Lista de comprovantes anexados a um boleto específico
const ComprovantesBoletoList = ({ romaneioId, boletoId, boletoNumero }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/romaneios/${romaneioId}/boletos/${boletoId}/comprovantes`);
      setItems(r.data || []);
    } catch (e) { setItems([]); }
    setLoading(false);
  }, [romaneioId, boletoId]);
  React.useEffect(() => { load(); }, [load]);
  const fmtDT = (iso) => {
    if (!iso) return '';
    try { const d = new Date(iso); return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };
  const roleLabel = (r) => r === 'admin' ? 'ADMIN' : r === 'cliente' ? 'CLIENTE' : r === 'vendedor' ? 'VENDEDOR' : (r || '?');
  const roleColor = (r) => r === 'admin' ? 'bg-purple-100 text-purple-700' : r === 'cliente' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700';
  if (loading) return null;
  if (items.length === 0) return null;
  return (
    <div className="mt-2 pl-2 border-l-2 border-emerald-300" data-testid={`comprovantes-boleto-${boletoId}`}>
      <p className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 mb-1">
        📎 Comprovantes anexados ({items.length}) — {boletoNumero}
      </p>
      <div className="space-y-1">
        {items.map((c) => {
          const href = c.source === 'payment_receipts'
            ? `${API}/comprovantes/${c.id}/download`
            : `${BACKEND_URL}${c.url}`;
          return (
            <div key={c.id} className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/30 rounded px-2 py-1 text-[11px]" data-testid={`comprovante-boleto-item-${c.id}`}>
              <a href={href} target="_blank" rel="noreferrer" className="flex-1 min-w-0 flex items-center gap-1 text-emerald-800 dark:text-emerald-200 hover:underline font-medium truncate" title={c.filename}>
                📄 <span className="truncate">{c.filename}</span>
              </a>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${roleColor(c.uploader_role)}`}>
                {roleLabel(c.uploader_role)}: {c.uploader_name || '?'}
              </span>
              <span className="text-[10px] text-gray-500 whitespace-nowrap" title={c.created_at}>
                {fmtDT(c.created_at)}
              </span>
              {c.status === 'pendente_revisao' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-amber-100 text-amber-800">PENDENTE</span>
              )}
              {c.status === 'aprovado' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-emerald-500 text-white">APROVADO</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Pequeno ícone inline WhatsApp (para modal email)
const MessageCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
  </svg>
);
